<template>
  <!-- ===== addModel.vue ===== -->
  <el-dialog
    :title="dialogTitle"
    :visible.sync="dialogVisible"
    width="68%"
    :close-on-click-modal="false"
    :before-close="handleClose"
    top="2vh"
    custom-class="addxyk-dialog"
  >
    <el-form
      ref="inboundForm"
      :model="formData"
      :rules="formRules"
      label-position="top"
      size="small"
      v-loading="loading"
    >
      <!-- ===== 调拨基本信息 ===== -->
      <div class="basic-info-section">
        <div class="section-header">
          <h4 class="section-title">调拨基本信息</h4>
          <p class="section-desc">{{ sectionDesc }}</p>
        </div>

        <!-- 第一行：3列 -->
        <el-row :gutter="16">
          <el-col :span="8">
            <el-form-item label="调拨类型" prop="allocateType" required>
              <el-select
                v-model="formData.allocateType"
                placeholder="请选择调拨类型"
                style="width:100%"
                :disabled="isViewMode || isSubmitMode || isReviewMode || isPassMode || isRollbackMode || isFastMode"
                @change="handleAllocateTypeChange"
              >
                <el-option
                  v-for="item in allocateTypeOptions"
                  :key="item.id"
                  :label="item.label"
                  :value="item.id"
                ></el-option>
              </el-select>
            </el-form-item>
          </el-col>

          <el-col :span="8">
            <el-form-item label="业务日期" prop="busDate" required>
              <el-date-picker
                v-model="formData.busDate"
                type="date"
                placeholder="选择日期"
                value-format="yyyy-MM-dd"
                format="yyyy-MM-dd"
                style="width:100%"
                :disabled="isViewMode || isSubmitMode || isReviewMode || isPassMode || isRollbackMode || isFastMode"
              ></el-date-picker>
            </el-form-item>
          </el-col>

          <el-col :span="8">
            <el-form-item label="操作人" prop="operationPerson">
              <el-input
                v-model="formData.operationPerson"
                placeholder="操作人"
                disabled
                style="width:100%"
              ></el-input>
            </el-form-item>
          </el-col>
        </el-row>

        <!-- 第二行：备注 -->
        <el-row :gutter="16">
          <el-col :span="24">
            <el-form-item label="备注" prop="remark">
              <el-input
                v-model="formData.remark"
                type="textarea"
                :rows="2"
                placeholder="请输入备注信息"
                :disabled="isViewMode || isSubmitMode || isReviewMode || isPassMode || isRollbackMode || isFastMode"
              ></el-input>
            </el-form-item>
          </el-col>
        </el-row>
      </div>

      <!-- ===== 药品明细信息 ===== -->
      <div class="medicine-detail-section">
        <div class="section-header">
          <h4 class="section-title">药品明细信息</h4>
          <div class="right-function-buttons">
            <!-- 扫码枪输入（仅新增/编辑模式） -->
            <div v-if="mode === 'add' || mode === 'edit'" class="scan-input-wrapper" style="display:inline-block; margin-right:10px;">
              <el-input
                v-model="outerScanInput"
                placeholder="请使用扫码枪扫描追溯码"
                clearable
                size="mini"
                @keyup.enter="handleOuterScanConfirm"
              >
                <template slot="append">
                  <el-button
                    type="primary"
                    icon="el-icon-Ssearch"
                    @click="handleOuterScanConfirm"
                    size="mini"
                    :loading="outerScanLoading"
                    :disabled="!outerScanInput || outerScanInput.trim() === ''"
                  >查询</el-button>
                </template>
              </el-input>
            </div>
            <!-- 添加药品按钮 -->
            <el-button
              v-if="mode === 'add' || mode === 'edit'"
              type="primary"
              icon="el-icon-plus"
              size="mini"
              @click="handleAddMedicine"
            >添加药品</el-button>
          </div>
        </div>

        <el-table
          :data="medicineList"
          style="width:100%"
          border
          size="mini"
          height="300"
          empty-text="暂无药品明细，请点击上方【添加药品】按钮"
        >
          <el-table-column type="index" label="#" width="50" align="center"></el-table-column>

          <!-- 类型标签 -->
          <el-table-column label="类型" width="60" align="center">
            <template slot-scope="scope">
              <el-tag
                v-if="scope.row.drugId && scope.row.drugType"
                :type="getDrugTypeTagType(scope.row.drugType)"
                size="mini"
                style="flex-shrink:0;"
              >
                {{ getDrugTypeText(scope.row.drugType) }}
              </el-tag>
            </template>
          </el-table-column>

          <!-- 药品名称（下拉选择） -->
          <el-table-column width="180" align="center">
            <template slot="header">
              <span style="color:red;">*</span> 药品名称
            </template>
            <template slot-scope="scope">
              <div class="drug-select-container" :key="scope.row.id || scope.$index">
                <el-select
                  v-model="scope.row.drugId"
                  filterable
                  placeholder="请选择药品"
                  size="mini"
                  style="width:100%"
                  @change="handleDrugChange(scope.row)"
                  clearable
                  :clearable="canClearDrug(scope.row)"
                  @clear="handleDrugClear(scope.row)"
                  remote
                  :remote-method="(query) => handleDrugSearch(query, scope.row)"
                  :remote="canRemoteSearch(scope.row)"
                  :loading="scope.row.drugLoading"
                  @focus="handleDrugFocus(scope.row)"
                  v-el-select-infinite-scroll="() => handleDrugScroll(scope.row)"
                  popper-class="drug-select-popper"
                  :disabled="!canSelectDrug(scope.row)"
                >
                  <el-option
                    v-for="drug in getDrugOptionsForRow(scope.row)"
                    :key="drug.id"
                    :label="getDrugDisplayLabel(drug, scope.row)"
                    :value="drug.drugId"
                  >
                    <div class="drug-option">
                      <div style="display:flex; align-items:center; gap:6px; flex-wrap:wrap;">
                        <el-tag
                          size="mini"
                          :type="getDrugTypeTagType(drug.drugType)"
                          style="flex-shrink:0;"
                        >
                          {{ getDrugTypeText(drug.drugType) }}
                        </el-tag>
                        <span class="drug-name" v-html="getHighlightedDrugName(drug, scope.row)"></span>
                      </div>
                      <template v-if="drug.drugSpecifications || drug.pacSpec || scope.row.spec">
                        <span class="drug-spec">规格：{{ drug.drugSpecifications || drug.pacSpec || scope.row.spec || '' }}</span>
                      </template>
                      <template v-if="drug.manufacturerName || scope.row.manufacturer">
                        <span class="drug-spec">生产厂家：{{ drug.manufacturerName || scope.row.manufacturer || '' }}</span>
                      </template>
                    </div>
                  </el-option>
                  <div v-if="showNoSearchResults(scope.row)" slot="empty" class="no-search-results">
                    <i class="el-icon-search"></i>
                    <span>未找到"{{ scope.row ? scope.row.drugSearchQuery : this.drugSearchQuery }}"相关药品</span>
                  </div>
                  <el-option
                    v-if="drugHasMore && !drugSearchLoading && canLoadMore(scope.row)"
                    :key="`load-more-${scope.row.id}-${Date.now()}`"
                    value="load-more"
                    label="点击加载更多"
                    @click.native="loadMoreDrugs(scope.row)"
                  >
                    <div class="load-more-option" @click.stop="loadMoreDrugs(scope.row)">
                      <i class="el-icon-loading" v-if="loadingMoreDrugs"></i>
                      <span v-else>点击加载更多</span>
                    </div>
                  </el-option>
                </el-select>
              </div>
            </template>
          </el-table-column>

          <!-- 申请调拨数量 -->
          <el-table-column width="120" align="center">
            <template slot="header">
              <span style="color:red;">*</span> 申请调拨数量
            </template>
            <template slot-scope="scope">
              <div class="qty-wrapper">
                <el-input
                  v-model="scope.row.apllyAmt"
                  size="mini"
                  @input="handleQuantityInput(scope.row)"
                  @blur="handleQuantityBlur(scope.row)"
                  class="qty-input"
                  :disabled="isViewMode || isSubmitMode || isReviewMode || isPassMode || isRollbackMode || isFastMode"
                ></el-input>
                <span class="unit-label">{{ scope.row.unitLabel || '' }}</span>
              </div>
            </template>
          </el-table-column>

          <!-- 库存类型 -->
          <el-table-column width="100" align="center">
            <template slot="header">
              <span style="color:red;">*</span> 库存类型
            </template>
            <template slot-scope="scope">
              <el-select
                v-model="scope.row.purcType"
                placeholder="类型"
                size="mini"
                @change="handlePurcTypeChange(scope.row)"
                style="width:100%"
                :disabled="isViewMode || isSubmitMode || isReviewMode || isPassMode || isRollbackMode || isFastMode"
              >
                <el-option label="最小单位" :value="1"></el-option>
                <el-option label="包装单位" :value="2"></el-option>
              </el-select>
            </template>
          </el-table-column>

          <!-- 包装数量 -->
          <el-table-column prop="pacCnt" width="100" align="center">
            <template slot="header">
              <span style="color:red;">*</span> 包装数量
            </template>
            <template slot-scope="scope">
              <el-input v-model="scope.row.pacCnt" size="mini" :disabled="true"></el-input>
            </template>
          </el-table-column>

          <!-- 批次号 -->
          <el-table-column prop="manuLotnum" width="120" align="center">
            <template slot="header">
              <span style="color:red;">*</span> 批次号
            </template>
            <template slot-scope="scope">
              <el-input
                v-model="scope.row.manuLotnum"
                placeholder="批号"
                size="mini"
                :disabled="true"
              ></el-input>
            </template>
          </el-table-column>

          <!-- 当前库存 -->
          <el-table-column prop="realPurcCnt" width="100" align="center">
            <template slot="header">当前库存</template>
            <template slot-scope="scope">
              <span>{{ scope.row.realPurcCnt || '--' }}</span>
            </template>
          </el-table-column>

          <!-- 追溯码库存 -->
          <el-table-column prop="traceStockQty" width="100" align="center">
            <template slot="header">追溯码库存</template>
            <template slot-scope="scope">
              <span>{{ scope.row.traceStockQty !== undefined ? scope.row.traceStockQty : '--' }}</span>
            </template>
          </el-table-column>

          <!-- 规格 -->
          <el-table-column prop="drugSpec" label="规格" width="120" show-overflow-tooltip>
            <template slot-scope="scope">
              <el-tooltip :content="scope.row.drugSpec || '--'" placement="top" :disabled="!scope.row.drugSpec || scope.row.drugSpec.length <= 10">
                <span>{{ scope.row.drugSpec ? (scope.row.drugSpec.length > 10 ? scope.row.drugSpec.substring(0,10) + '...' : scope.row.drugSpec) : '--' }}</span>
              </el-tooltip>
            </template>
          </el-table-column>

          <!-- 成本单价 -->
          <el-table-column prop="purcPric" label="成本" width="100" align="right">
            <template slot-scope="scope">
              <span>¥{{ scope.row.purcPric ? formatDecimal(scope.row.purcPric, 8) : '0.00' }}</span>
            </template>
          </el-table-column>

          <!-- 零售单价 -->
          <el-table-column prop="salePric" label="零售" width="100" align="right">
            <template slot-scope="scope">
              <span>¥{{ scope.row.salePric ? formatDecimal(scope.row.salePric, 8) : '0.00' }}</span>
            </template>
          </el-table-column>

          <!-- 生产期 -->
          <el-table-column prop="manuDate" label="生产期" width="120" align="center">
            <template slot-scope="scope">
              <span>{{ scope.row.manuDate || '--' }}</span>
            </template>
          </el-table-column>

          <!-- 有效期 -->
          <el-table-column prop="expyDate" label="有效期" width="120" align="center">
            <template slot-scope="scope">
              <span>{{ scope.row.expyDate || '--' }}</span>
            </template>
          </el-table-column>

          <!-- 生产厂商 -->
          <el-table-column prop="prodentpName" label="生产厂商" width="140" align="center" show-overflow-tooltip>
            <template slot-scope="scope">
              <span>{{ scope.row.prodentpName || '--' }}</span>
            </template>
          </el-table-column>

          <!-- 操作列 -->
          <el-table-column
            v-if="!isViewMode && !isSubmitMode && !isReviewMode && !isPassMode && !isRollbackMode && !isFastMode"
            label="操作"
            width="140"
            align="center"
            fixed="right"
          >
            <template slot-scope="scope">
              <el-tooltip
                v-if="scope.row.isBar === 1"
                :content="getTraceCodeTooltip(scope.row)"
                placement="top"
                :disabled="!getTraceCodeTooltip(scope.row)"
              >
                <span style="display:inline-block;">
                  <el-button
                    v-if="scope.row.isBar === 1"
                    type="text"
                    size="mini"
                    @click="openTraceCodeSupplement(scope.row, scope.$index)"
                    :disabled="!canTraceCode(scope.row)"
                    style="margin-right:5px;"
                  >{{ scope.row.traceCodeButtonText || '追溯码' }}</el-button>
                </span>
              </el-tooltip>
              <el-button
                type="text"
                size="mini"
                style="color:#F56C6C;"
                @click="handleRemoveMedicine(scope.$index, scope.row)"
              >移除</el-button>
            </template>
          </el-table-column>
        </el-table>

        <!-- 汇总信息 -->
        <div class="summary-info">
          <el-row :gutter="20">
            <el-col :span="6">
              <div class="summary-item">
                <span class="label">药品数量：</span>
                <span class="value">{{ medicineList.length }}</span>
              </div>
            </el-col>
            <el-col :span="6">
              <div class="summary-item">
                <span class="label">总调拨数量：</span>
                <span class="value">{{ totalApllyQty }}</span>
              </div>
            </el-col>
            <el-col :span="6">
              <div class="summary-item">
                <span class="label">总成本：</span>
                <span class="value">¥{{ formatCurrency(totalCost) }}</span>
              </div>
            </el-col>
            <el-col :span="6">
              <div class="summary-item">
                <span class="label">总零售：</span>
                <span class="value">¥{{ formatCurrency(totalRetail) }}</span>
              </div>
            </el-col>
          </el-row>
        </div>
      </div>
    </el-form>

    <!-- 底部按钮 -->
    <span slot="footer" class="dialog-footer">
      <template v-if="isViewMode">
        <el-button size="small" @click="handleClose">关闭</el-button>
      </template>
      <template v-else-if="mode === 'add'">
        <el-button size="small" @click="handleClose">取消</el-button>
        <el-button type="primary" size="small" @click="handleCreate" :loading="saving" :disabled="medicineList.length === 0">确认创建</el-button>
      </template>
      <template v-else-if="mode === 'edit'">
        <el-button size="small" @click="handleClose">关闭</el-button>
        <el-button type="primary" size="small" @click="handleSave" :loading="saving">保存</el-button>
        <el-button type="success" size="small" @click="handleSaveAndSubmit" :loading="saving">保存提交</el-button>
      </template>
      <template v-else-if="mode === 'submit'">
        <el-button size="small" @click="handleClose">关闭</el-button>
        <el-button type="primary" size="small" @click="handleSubmitConfirm" :loading="saving">确认提交</el-button>
      </template>
      <template v-else-if="mode === 'review'">
        <el-button size="small" @click="handleClose">关闭</el-button>
        <el-button type="warning" size="small" @click="handleRollbackDraft" :loading="saving">回退草稿</el-button>
        <el-button type="primary" size="small" @click="handleReviewConfirm" :loading="saving">确认审核</el-button>
      </template>
      <template v-else-if="mode === 'pass'">
        <el-button size="small" @click="handleClose">关闭</el-button>
        <el-button type="warning" size="small" @click="handleRollbackReview" :loading="saving">回退审核</el-button>
        <el-button type="success" size="small" @click="handlePassConfirm" :loading="saving">确认入库</el-button>
      </template>
      <template v-else-if="mode === 'rollback'">
        <el-button size="small" @click="handleClose">关闭</el-button>
        <el-button type="warning" size="small" @click="handleRollbackConfirm" :loading="saving">确认回退</el-button>
      </template>
      <template v-else-if="mode === 'fast'">
        <el-button size="small" @click="handleClose">关闭</el-button>
        <el-button type="primary" size="small" @click="handleFastTransferConfirm" :loading="saving">确认无误，快速调拨</el-button>
      </template>
    </span>

    <!-- 新增供应商模态框（调拨单不需要，但保留以防万一） -->
    <el-dialog title="新增供应商" :visible.sync="supplierDialogVisible" width="500px" :close-on-click-modal="false">
      <el-form ref="supplierForm" :model="supplierForm" :rules="supplierRules" label-width="100px" size="small">
        <el-form-item label="供应商名称" prop="supplierName" required>
          <el-input v-model="supplierForm.supplierName" placeholder="请输入供应商名称"></el-input>
        </el-form-item>
        <el-form-item label="联系人" prop="contact" required>
          <el-input v-model="supplierForm.contact" placeholder="请输入联系人"></el-input>
        </el-form-item>
        <el-form-item label="联系人电话" prop="contactPhone" required>
          <el-input v-model="supplierForm.contactPhone" placeholder="请输入联系人电话"></el-input>
        </el-form-item>
        <el-form-item label="供应商地址" prop="address">
          <el-input v-model="supplierForm.address" placeholder="请输入供应商地址"></el-input>
        </el-form-item>
        <el-form-item label="码上放心-企业ID" prop="entId">
          <el-input v-model="supplierForm.entId" placeholder="企业ID"></el-input>
        </el-form-item>
        <el-form-item label="许可证号" prop="permitNumber">
          <el-input v-model="supplierForm.permitNumber" placeholder="请输入许可证号"></el-input>
        </el-form-item>
        <el-form-item label="状态" prop="status" required>
          <el-select v-model="supplierForm.status" placeholder="请选择状态" style="width:100%">
            <el-option label="禁用" :value="0"></el-option>
            <el-option label="启用" :value="1"></el-option>
          </el-select>
        </el-form-item>
        <el-form-item label="备注" prop="remark">
          <el-input v-model="supplierForm.remark" type="textarea" :rows="2" placeholder="请输入备注"></el-input>
        </el-form-item>
      </el-form>
      <span slot="footer" class="dialog-footer">
        <el-button size="small" @click="supplierDialogVisible = false">取消</el-button>
        <el-button type="primary" size="small" @click="handleSaveSupplier" :loading="supplierSaving">保存</el-button>
      </span>
    </el-dialog>

    <!-- ========== 追溯码相关弹窗 ========== -->
    <!-- 追溯码录入模态框（主） -->
    <el-dialog
      :title="traceCodeDialogTitle"
      :visible.sync="traceCodeDialogVisible"
      width="80%"
      :close-on-click-modal="false"
      :before-close="handleTraceCodeDialogClose"
      top="5vh"
      custom-class="trace-code-dialog"
    >
      <div class="dialog-header" slot="title">
        <span class="dialog-title-text">{{ traceCodeDialogTitle }}</span>
      </div>
      <div class="trace-code-dialog-content">
        <!-- 功能按钮区域 -->
        <div class="function-buttons-section">
          <div class="function-button-group">
            <div class="left-function-area">
              <div class="scan-input-area">
                <h4 class="scan-area-title">扫码枪录入区域</h4>
                <div class="scan-input-wrapper">
                  <el-input
                    v-model="scanInput"
                    placeholder="请使用扫码枪扫描追溯码，按回车键确认"
                    clearable
                    @keyup.enter="handleScanInput"
                    size="small"
                    style="width:100%;"
                  >
                    <template slot="append">
                      <el-button type="primary" icon="el-icon-guahao" @click="handleScanInput" size="small">确认</el-button>
                    </template>
                  </el-input>
                </div>
                <div class="scan-tips">
                  <i class="el-icon-info"></i> 提示：扫描追溯码后按回车键或点击确认按钮
                </div>
              </div>
            </div>
            <div class="right-function-area">
              <el-tooltip content="清空所有追溯码" placement="top">
                <div class="function-button-item" @click="handleClearTraceCode">
                  <i class="el-icon-delete function-icon"></i>
                  <span class="function-text">清空</span>
                </div>
              </el-tooltip>
              <el-tooltip content="导入追溯码" placement="top">
                <div class="function-button-item" @click="handleImportTraceCode">
                  <i class="el-icon-upload2 function-icon"></i>
                  <span class="function-text">导入</span>
                </div>
              </el-tooltip>
              <el-tooltip content="导出追溯码" placement="top">
                <div class="function-button-item" @click="handleExportTraceCode">
                  <i class="el-icon-download function-icon"></i>
                  <span class="function-text">导出</span>
                </div>
              </el-tooltip>
            </div>
          </div>
        </div>

        <div class="summary-info-section" v-if="currentTraceMedicine">
          <el-row :gutter="20">
            <el-col :span="6">
              <div class="summary-item">
                <span class="label">药品名称：</span>
                <span class="value">{{ currentTraceMedicine.drugName || '--' }}</span>
              </div>
            </el-col>
            <el-col :span="6">
              <div class="summary-item">
                <span class="label">调拨数量：</span>
                <span class="value">{{ currentTraceMedicine.apllyAmt || 0 }}</span>
              </div>
            </el-col>
            <el-col :span="6">
              <div class="summary-item">
                <span class="label">包装数量：</span>
                <span class="value">{{ currentTraceMedicine.pacCnt || 0 }}</span>
              </div>
            </el-col>
            <el-col :span="6">
              <div class="summary-item">
                <span class="label">已录入数量：</span>
                <span class="value" :class="{ 'warning': totalTraceQty !== currentTraceMedicine.apllyAmt }">
                  {{ totalTraceQty }}
                  <span v-if="totalTraceQty !== currentTraceMedicine.apllyAmt" style="color: #E6A23C; margin-left:5px;">
                    (还需录入 {{ currentTraceMedicine.apllyAmt - totalTraceQty }} 个)
                  </span>
                </span>
              </div>
            </el-col>
          </el-row>
        </div>

        <div class="trace-code-table-section">
          <el-table
            :data="traceCodeList"
            style="width:100%"
            border
            size="mini"
            height="300"
            v-loading="traceCodeLoading"
            class="trace-code-table"
          >
            <el-table-column label="操作" width="80" align="center" fixed="left">
              <template slot-scope="scope">
                <el-button type="text" size="mini" icon="el-icon-setting" @click="handleTraceCodeSetting(scope.row, scope.$index)" title="设置"></el-button>
              </template>
            </el-table-column>
            <el-table-column prop="traceCode" label="追溯码" min-width="200" align="center" fixed="left">
              <template slot-scope="scope">
                <el-input v-model="scope.row.traceCode" placeholder="请输入追溯码" size="mini" clearable @blur="validateTraceCode(scope.row)"></el-input>
              </template>
            </el-table-column>
            <el-table-column prop="drugName" label="药品名称" min-width="150" align="center" show-overflow-tooltip>
              <template slot-scope="scope">
                <span>{{ scope.row.drugName || '--' }}</span>
              </template>
            </el-table-column>
            <el-table-column prop="spec" label="规格" width="120" align="center">
              <template slot-scope="scope">
                <span>{{ scope.row.spec || '--' }}</span>
              </template>
            </el-table-column>
            <el-table-column prop="availableQty" label="可用数量" width="100" align="center">
              <template slot-scope="scope">
                <el-input-number
                  v-model="scope.row.availableQty"
                  :min="1"
                  :max="getMaxAvailableQty(scope.row)"
                  :step="1"
                  size="mini"
                  style="width:100%"
                  @change="handleAvailableQtyChange(scope.row)"
                ></el-input-number>
                <div v-if="scope.row.availableQty > getMaxAvailableQty(scope.row)" style="color:#F56C6C;font-size:12px;">超出最大数量</div>
              </template>
            </el-table-column>
            <el-table-column prop="manufacturer" label="生产厂家" width="150" align="center" show-overflow-tooltip>
              <template slot-scope="scope">
                <span>{{ scope.row.manufacturer || '--' }}</span>
              </template>
            </el-table-column>
            <el-table-column prop="approvalLicenceNo" label="批准文号" width="150" align="center" show-overflow-tooltip>
              <template slot-scope="scope">
                <span>{{ scope.row.approvalLicenceNo || '--' }}</span>
              </template>
            </el-table-column>
            <el-table-column prop="batchNo" label="生产批号" width="150" align="center">
              <template slot-scope="scope">
                <span>{{ scope.row.batchNo || '--' }}</span>
              </template>
            </el-table-column>
            <el-table-column prop="produceDate" label="生产日期" width="120" align="center">
              <template slot-scope="scope">
                <span>{{ scope.row.produceDate || '--' }}</span>
              </template>
            </el-table-column>
            <el-table-column prop="expireDate" label="有效期" width="120" align="center">
              <template slot-scope="scope">
                <span>{{ scope.row.expireDate || '--' }}</span>
              </template>
            </el-table-column>
            <el-table-column prop="codeStatus" label="码状态" width="100" align="center">
              <template slot-scope="scope">
                <el-tag :type="getCodeStatusType(scope.row.codeStatus)" size="mini">{{ scope.row.codeStatus || '--' }}</el-tag>
              </template>
            </el-table-column>
            <el-table-column prop="remark" label="备注" min-width="150" align="center">
              <template slot-scope="scope">
                <el-input v-model="scope.row.remark" placeholder="请输入备注" size="mini" clearable></el-input>
              </template>
            </el-table-column>
            <el-table-column label="操作" width="80" align="center" fixed="right">
              <template slot-scope="scope">
                <el-button type="text" size="mini" style="color:#F56C6C;" @click="handleRemoveTraceCode(scope.$index)">删除</el-button>
              </template>
            </el-table-column>
          </el-table>
        </div>
      </div>
      <div slot="footer" class="trace-code-dialog-footer">
        <el-button size="small" @click="handleCancelTraceCode">取消</el-button>
        <el-button type="primary" size="small" @click="handleConfirmTraceCode" :loading="traceCodeLoading" :disabled="!canConfirmTraceCode">确定</el-button>
      </div>
    </el-dialog>

    <!-- 追溯码补录模态框 -->
    <el-dialog
      title="追溯码补录"
      :visible.sync="traceCodeSupplementDialogVisible"
      width="80%"
      :close-on-click-modal="false"
      top="5vh"
      @close="handleCancelTraceCodeSupplement"
    >
      <div class="trace-code-supplement-dialog-content">
        <div class="add-button-section">
          <el-button type="primary" icon="el-icon-plus" size="small" @click="handleAddSupplementTraceCode">添加新的追溯码</el-button>
        </div>
        <div class="trace-code-table-section">
          <el-table
            :data="traceCodeSupplementList"
            style="width:100%"
            border
            size="mini"
            height="300"
          >
            <el-table-column prop="traceCode" label="追溯码" min-width="200" align="center">
              <template slot-scope="scope">
                <el-input
                  v-model="scope.row.traceCode"
                  placeholder="请输入追溯码"
                  size="mini"
                  clearable
                  :disabled="!scope.row.isEditing"
                  @blur="handleTraceCodeBlur(scope.row, scope.$index)"
                ></el-input>
              </template>
            </el-table-column>
            <el-table-column prop="drugName" label="药品名称" width="150" align="center">
              <template slot-scope="scope">
                <span>{{ scope.row.drugName }}</span>
              </template>
            </el-table-column>
            <el-table-column prop="spec" label="规格" width="120" align="center">
              <template slot-scope="scope">
                <span>{{ scope.row.spec }}</span>
              </template>
            </el-table-column>
            <el-table-column prop="availableQty" label="可用数量" width="150" align="center">
              <template slot-scope="scope">
                <el-input
                  v-model="scope.row.availableQty"
                  size="mini"
                  style="width:100%"
                  :disabled="!canEditAvailableQty(scope.row)"
                  @change="handleAvailableQtyChange(scope.row, scope.$index)"
                  @blur="handleAvailableQtyBlur(scope.row, scope.$index)"
                ></el-input>
                <div v-if="currentTraceMedicine && currentTraceMedicine.pacCnt" class="available-qty-tip">包装数量: {{ currentTraceMedicine.pacCnt }}</div>
              </template>
            </el-table-column>
            <el-table-column prop="manufacturer" label="生产厂家" width="150" align="center">
              <template slot-scope="scope">
                <span>{{ scope.row.manufacturer }}</span>
              </template>
            </el-table-column>
            <el-table-column prop="batchNo" label="生产批号" width="150" align="center">
              <template slot-scope="scope">
                <span>{{ scope.row.batchNo }}</span>
              </template>
            </el-table-column>
            <el-table-column prop="produceDate" label="生产日期" width="120" align="center">
              <template slot-scope="scope">
                <span>{{ scope.row.produceDate }}</span>
              </template>
            </el-table-column>
            <el-table-column prop="expireDate" label="有效期" width="120" align="center">
              <template slot-scope="scope">
                <span>{{ scope.row.expireDate }}</span>
              </template>
            </el-table-column>
            <el-table-column label="操作" width="150" align="center" fixed="right">
              <template slot-scope="scope">
                <template v-if="scope.row.isEditing">
                  <el-button type="text" size="mini" style="color:#909399;" @click="handleCancelSupplementEdit(scope.$index)">取消</el-button>
                  <el-button type="text" size="mini" style="color:#409EFF;margin-left:5px;" @click="handleSaveSupplementRow(scope.$index)">保存</el-button>
                </template>
                <template v-else>
                  <el-button type="text" size="mini" style="color:#409EFF;" @click="handleEditSupplementRow(scope.$index)">编辑</el-button>
                  <el-button type="text" size="mini" style="color:#F56C6C;margin-left:5px;" @click="handleRemoveSupplementRow(scope.$index)">移除</el-button>
                </template>
              </template>
            </el-table-column>
          </el-table>
        </div>
      </div>
      <div slot="footer" class="dialog-footer">
        <el-button size="small" @click="handleCancelTraceCodeSupplement">取消</el-button>
        <el-button type="primary" size="small" @click="handleConfirmTraceCodeSupplement">确定</el-button>
      </div>
    </el-dialog>

    <!-- 追溯码录入模态框（通过顶部按钮打开） -->
    <el-dialog
      title="追溯码录入"
      :visible.sync="traceCodeInputDialogVisible"
      width="60%"
      :close-on-click-modal="false"
      top="5vh"
      class="trace-code-input-dialog"
      @close="handleCloseTraceCodeInput"
    >
      <div class="trace-code-input-dialog-content">
        <div class="medicine-form-section" v-if="traceCodeDetailData">
          <h4 class="section-title">药品信息（追溯码：{{ traceCodeInputForm.traceCode || '无' }}）</h4>
          <el-descriptions :column="2" border size="small" class="trace-code-descriptions">
            <el-descriptions-item label="药品名称" label-class-name="description-label">
              <el-input v-model="traceCodeInputForm.drugName" placeholder="药品名称" :disabled="true" class="description-content"></el-input>
            </el-descriptions-item>
            <el-descriptions-item label="规格" label-class-name="description-label">
              <el-input v-model="traceCodeInputForm.spec" disabled class="description-content"></el-input>
            </el-descriptions-item>
            <el-descriptions-item label="批准文号" label-class-name="description-label">
              <el-input v-model="traceCodeInputForm.approvalLicenceNo" placeholder="批准文号" :disabled="true" class="description-content"></el-input>
            </el-descriptions-item>
            <el-descriptions-item label="包装数量" label-class-name="description-label">
              <el-input-number v-model="traceCodeInputForm.pacCnt" :min="1" :step="1" size="small" class="description-content" :disabled="true"></el-input-number>
            </el-descriptions-item>
            <el-descriptions-item label="生产厂家" label-class-name="description-label">
              <el-input v-model="traceCodeInputForm.manufacturer" placeholder="生产厂家" :disabled="true" class="description-content"></el-input>
            </el-descriptions-item>
            <el-descriptions-item label="生产批号" label-class-name="description-label">
              <el-input v-model="traceCodeInputForm.manuLotnum" :disabled="true" class="description-content"></el-input>
            </el-descriptions-item>
            <el-descriptions-item label="有效期" label-class-name="description-label">
              <el-input v-model="traceCodeInputForm.expireDate" :disabled="true" class="description-content"></el-input>
            </el-descriptions-item>
            <el-descriptions-item label="生产日期" label-class-name="description-label">
              <el-input v-model="traceCodeInputForm.produceDate" :disabled="true" class="description-content"></el-input>
            </el-descriptions-item>
            <el-descriptions-item label="剂型" label-class-name="description-label">
              <el-input v-model="traceCodeInputForm.dosageFormName" disabled class="description-content"></el-input>
            </el-descriptions-item>
          </el-descriptions>
        </div>
        <div v-if="getSomeWareLoading" class="loading-section">
          <i class="el-icon-loading"></i>
          <span>正在查询药品信息...</span>
        </div>
      </div>
      <span slot="footer" class="trace-code-dialog-footer">
        <el-button size="small" @click="handleCancelTraceCodeInput">取消</el-button>
        <el-tooltip v-if="showBottomAddButton" :content="getAddButtonTooltip()" placement="top" :disabled="!isAddButtonDisabled">
          <el-button type="warning" size="small" @click="openAddDrugDialog()" :disabled="isAddButtonDisabled">去添加</el-button>
        </el-tooltip>
        <el-button type="primary" size="small" @click="handleConfirmTraceCodeInput" :loading="traceCodeLoading" :disabled="!canConfirmTraceCodeInput">确定</el-button>
      </span>
    </el-dialog>

    <!-- ===== 新增：追溯码详情模态框（扫码查询后展示） ===== -->
    <el-dialog
      title="追溯码详情"
      :visible.sync="traceCodeDetailDialogVisible"
      width="70%"
      :close-on-click-modal="false"
      top="5vh"
      append-to-body
    >
      <div class="trace-code-detail-container" v-if="currentTraceDetailData">
        <el-descriptions
          :column="4"
          border
          size="small"
          class="trace-code-descriptions"
        >
          <el-descriptions-item label="当前追溯码" :span="1">
            {{ currentTraceCode }}
          </el-descriptions-item>
          <el-descriptions-item label="药品名称" :span="1">
            {{ currentTraceDetailData.drugEntBaseDTO?.physicName || '--' }}
          </el-descriptions-item>
          <el-descriptions-item label="类型" :span="1">
            {{ currentTraceDetailData.drugEntBaseDTO?.physicTypeDesc || '--' }}
          </el-descriptions-item>
          <el-descriptions-item label="规格" :span="1">
            {{ currentTraceDetailData.drugEntBaseDTO?.pkgSpecCrit || '--' }}
          </el-descriptions-item>

          <el-descriptions-item label="批准文号" :span="1">
            {{ currentTraceDetailData.drugEntBaseDTO?.approvalLicenceNo || '--' }}
          </el-descriptions-item>
          <el-descriptions-item label="包装数量" :span="1">
            {{ currentTraceDetailData.drugEntBaseDTO?.prepnSpec || '--' }}
          </el-descriptions-item>
          <el-descriptions-item label="剂型" :span="1">
            {{ currentTraceDetailData.drugEntBaseDTO?.prepnTypeDesc || '--' }}
          </el-descriptions-item>
          <el-descriptions-item label="批次号" :span="1">
            {{ currentTraceDetailData.codeProduceInfoDTO?.produceInfoList?.[0]?.batchNo || '--' }}
          </el-descriptions-item>

          <el-descriptions-item label="生产厂商" :span="2">
            {{ currentTraceDetailData.pUserEntDTO?.entName || '--' }}
          </el-descriptions-item>
          <el-descriptions-item label="有效期" :span="2">
            {{ currentTraceDetailData.drugEntBaseDTO?.exprie || '--' }}
          </el-descriptions-item>
        </el-descriptions>
      </div>

      <span slot="footer" class="dialog-footer">
        <el-button size="mini" @click="handleCancelTraceCodeDetail">取消</el-button>
        <el-button type="primary" size="mini" @click="handleConfirmTraceCodeDetail">确认录入</el-button>
      </span>
    </el-dialog>

    <!-- ===== 过单进度模态框 ===== -->
    <el-dialog
      title="追溯码上传进度"
      :visible.sync="passProgressDialogVisible"
      width="700px"
      :close-on-click-modal="false"
      :close-on-press-escape="false"
      class="trace-code-upload-dialog"
    >
      <div class="upload-progress-dialog">
        <el-steps 
          :active="traceCodeUploadStep" 
          align-center
          finish-status="success"
          class="upload-steps"
        >
          <el-step
            v-for="(step, index) in traceCodeUploadSteps"
            :key="index"
            :title="step.title"
            :description="step.description"
            :status="getStepStatus(index)"
          />
        </el-steps>
        
        <div class="upload-details">
          <div class="details-title">上传详情：</div>
          <div 
            class="details-content" 
            ref="uploadDetailsContainer"
            style="max-height: 200px; overflow-y: auto;"
          >
            <div 
              v-for="(detail, index) in traceCodeUploadDetails" 
              :key="index"
              class="detail-item"
              :class="detail.type"
            >
              <i :class="getDetailIcon(detail.type)"></i>
              <span>{{ detail.message }}</span>
              <span class="detail-time">{{ detail.time }}</span>
            </div>
          </div>
        </div>
        
        <div v-if="traceCodeUploadStep < traceCodeUploadSteps.length" class="upload-progress">
          <el-progress
            :percentage="uploadProgress"
            :status="uploadStatus"
            :show-text="false"
          />
        </div>
      </div>
      
      <template slot="footer" class="upload-dialog-footer">
        <div v-if="showRetryButton && traceCodeHasError">
          <el-button @click="closeTraceCodeUploadDialog" type="primary">我已知晓</el-button>
        </div>
        <div v-else-if="traceCodeUploadStep === 4 && !traceCodeHasError && msfxUploadSuccess">
          <!-- 自动关闭 -->
        </div>
        <div v-else-if="traceCodeUploadStep === 4 && !traceCodeHasError && !msfxUploadSuccess">
          <el-button @click="closeTraceCodeUploadDialog" type="primary">我已知晓</el-button>
        </div>
        <div v-else>
          <el-button @click="handleUploadCancel">取消</el-button>
        </div>
      </template>
    </el-dialog>
  </el-dialog>
</template>

<script>
// ===== 追溯码相关 API =====
import {
  msfxCodeDetail,
  msfxGetEntInfoList,
  msfListUpoutDetail,
  msfxUploadInoutBill,
  msfxUploadRetail,
  barcodeCheckBar,
  barcodeScreen,
  getSomeWare
} from "@/api/systemCatalogue/Western.js";
import { getSystemList } from "@/api/systemParameter/systemParameter";

// ===== 调拨相关 API =====
import {
  dpurcallocatePage,
  selectDropDownList,
  Adddpurcallocate,
  changedpurcallocate,
  getdpurcallocate,
  dpurcallocateJoin,
  Canceldpurcallocate,
  editdpurcallocate,
  selBar
} from "@/api/systemCatalogue/Western.js";
// 西药房
import {
  dugdpurctablePage
} from "@/api/MedicenHouse/Western.js";
import {
  selectDrug,
  selectYFtoYKDrug,
} from "@/api/PSI/shopReturn.js";

import { addSupplierEntity } from "@/api/supplier.js";
import { fastTransfer } from "@/api/systemCatalogue/Western.js";

// ===== 自定义指令 =====
const elSelectInfiniteScroll = {
  bind(el, binding) {
    const SELECTWRAPPER_DOM = el.querySelector('.el-select-dropdown .el-select-dropdown__wrap');
    if (SELECTWRAPPER_DOM) {
      SELECTWRAPPER_DOM.addEventListener('scroll', function() {
        const condition = this.scrollHeight - this.scrollTop - 1 <= this.clientHeight;
        if (condition) {
          binding.value();
        }
      });
    }
  }
};
const elTooltip = {
  inserted(el, binding) {
    el.setAttribute('title', binding.value);
  }
};

export default {
  name: 'AddxykModal',
  directives: {
    'el-select-infinite-scroll': elSelectInfiniteScroll,
    'el-tooltip': elTooltip
  },
  props: {
    visible: { type: Boolean, default: false },
    mode: { type: String, default: 'add' },
    rowData: { type: Object, default: () => ({}) },
    putinTypeList: { type: Array, default: () => [] }
  },
  data() {
    let userInfo = {};
    try {
      userInfo = JSON.parse(localStorage.getItem('userInfo') || '{}');
    } catch (e) {}

    return {
      dialogVisible: false,
      loading: false,
      saving: false,
      supplierLoading: false,
      supplierSaving: false,
      supplierDialogVisible: false,

      allocateTypeOptions: [
        { id: 1, label: '药库 => 药房' },
        { id: 2, label: '药房 => 药库' }
      ],

      defaultDrugOptions: [],
      searchTimer: null,
      drugSearchLoading: false,
      loadingMoreDrugs: false,
      drugOptions: [],
      drugPageNum: 1,
      drugPageSize: 50,
      drugHasMore: true,
      drugSearchQuery: '',
      allDrugOptions: [],

      formData: {
        allocateType: 1,
        busDate: new Date().toISOString().split('T')[0],
        operationPerson: userInfo.staffName,
        remark: '',
        shedId: 1,
        phayId: 3
      },
      formRules: {
        allocateType: [{ required: true, message: '请选择调拨类型', trigger: 'change' }],
        busDate: [{ required: true, message: '请选择业务日期', trigger: 'change' }]
      },

      supplierForm: {
        address: "", clinicId: 0, contact: "", contactPhone: "", id: 0,
        payAmt: 0, permitNumber: "", entId: "", receAmt: 0, remark: "",
        status: 1, supplierName: ""
      },
      supplierRules: {
        supplierName: [{ required: true, message: '供应商名称为必填项', trigger: 'blur' }],
        contact: [{ required: true, message: '联系人为必填项', trigger: 'blur' }],
        contactPhone: [{ required: true, message: '联系人电话为必填项', trigger: 'blur' }],
        status: [{ required: true, message: '请选择状态', trigger: 'change' }]
      },
      supplierList: [],

      medicineList: [],
      totalApllyQty: 0,
      totalCost: 0,
      totalRetail: 0,

      currentSearchKeyword: '',
      highlightColor: '#ff6700',

      // ===== 追溯码相关 =====
      traceCodeDialogVisible: false,
      currentTraceMedicine: null,
      traceCodeList: [],
      traceCodeDialogTitle: '追溯码录入',
      traceCodeLoading: false,
      scanInput: '',
      totalTraceQty: 0,

      traceCodeSupplementDialogVisible: false,
      traceCodeSupplementList: [],

      traceCodeInputDialogVisible: false,
      traceCodeInputForm: {
        traceCode: '',
        drugName: '',
        spec: '',
        inboundQty: 1,
        purcType: 2,
        pacCnt: 1,
        manuLotnum: '',
        manufacturer: '',
        manufacturerId: '',
        approvalLicenceNo: '',
        produceDate: '',
        expireDate: '',
        refEntId: '',
        remark: '',
        dosageFormName: '',
        pacUnt: '',
        dosUnt: ''
      },
      traceCodeDetailData: null,
      getSomeWareData: null,
      getSomeWareLoading: false,
      outerScanInput: '',
      outerScanLoading: false,
      showBottomAddButton: false,

      addDrugDialogVisible: false,
      addDrugDialogData: {
        drugName: '', spec: '', pacCnt: 1,
        approvalLicenceNo: '', manufacturer: '', remark: ''
      },

      systemParameterList: [],
      msfxEntId: '',
      systemParameterLoading: false,

      // ===== 新增：追溯码详情模态框相关 =====
      traceCodeDetailDialogVisible: false,
      currentTraceDetailData: null,
      currentTraceCode: '',

      // ===== 过单进度 =====
      passProgressDialogVisible: false,
      traceCodeUploadStep: 0,
      traceCodeUploadSteps: [
        { title: '检查追溯码', description: '正在检查是否有药品需要录入追溯码...' },
        { title: '接入码上放心', description: '正在接入码上放心平台接口...' },
        { title: '上传追溯码', description: '正在上传调拨明细追溯码数据...' },
        { title: '完成上传', description: '追溯码已上传至码上放心平台。' },
        { title: '完成调拨', description: '已完成调拨。' }
      ],
      traceCodeUploadDetails: [],
      traceCodeHasError: false,
      showRetryButton: false,
      uploadRetryCount: 0,
      msfxUploading: false,
      autoCloseTimer: null,
      msfxUploadError: '',
      msfxUploadSuccess: true,

      passCurrentStep: 0,
      passLoading: false,
      needUploadTraceCode: false,
      traceCodeCount: 0,
      traceCodeString: '',
      needUploadDrugs: [],
      progressTimer: null,
      totalProgress: 0,
      showCloseButton: false,

      allowReselectAll: false,
      originalDrugDetails: null,
      adminModeWindow: false,
      adminKeyHandler: null,
      pendingTraceCode: '',
      adminTestDialogVisible: false,
      adminTestList: [],
      adminTestLoading: false,
      adminWindowTimer: null,

      quantityDebounceTimer: null,
    };
  },
  computed: {
    dialogTitle() {
      const base = this.formData.allocateType === 1 ? '【药库】调拨单' : '【药房】调拨单';
      if (this.mode === 'add') return `新建${base}`;
      if (this.mode === 'edit') return `修改${base} - ${this.rowData?.docNum || ''}`;
      if (this.mode === 'view') return `查看${base} - ${this.rowData?.docNum || ''}`;
      if (this.mode === 'submit') return `提交${base} - ${this.rowData?.docNum || ''}`;
      if (this.mode === 'review') return `审核${base} - ${this.rowData?.docNum || ''}`;
      if (this.mode === 'pass') return `过单${base} - ${this.rowData?.docNum || ''}`;
      if (this.mode === 'rollback') return `回退${base} - ${this.rowData?.docNum || ''}`;
      if (this.mode === 'fast') return `快速调拨 - ${this.rowData?.docNum || ''}`;
      return base;
    },
    sectionDesc() {
      const type = this.formData.allocateType;
      if (type === 1) {
        return '当前操作为：调拨 药库 至 药房，草稿可进行查询编辑修改，当提交完成后会进行’审核，过单‘等流程，当完成过单后，调拨完成。';
      } else {
        return '当前操作为：调拨 药房 至 药库，草稿可进行查询编辑修改，当提交完成后会进行’审核，过单‘等流程，当完成过单后，调拨完成。';
      }
    },
    warehouseName() { return '西药'; },
    isViewMode() { return this.mode === 'view'; },
    isEditMode() { return this.mode === 'edit'; },
    isSubmitMode() { return this.mode === 'submit'; },
    isReviewMode() { return this.mode === 'review'; },
    isPassMode() { return this.mode === 'pass'; },
    isRollbackMode() { return this.mode === 'rollback'; },
    isFastMode() { return this.mode === 'fast'; },
    isAddMode() { return this.mode === 'add'; },

    isAddButtonDisabled() { return this.drugExistsInSystem; },
    getAddButtonTooltip() {
      if (this.isAddButtonDisabled) return '当前药品已存在于系统目录中无需再次添加！';
      return '';
    },
    canConfirmTraceCodeInput() { return this.validateTraceCodeInputForm(); },
    canConfirmTraceCode() { return this.canConfirmTraceCodeInput; },
    drugExistsInSystem() {
      return this.getSomeWareData && this.getSomeWareData !== '' && (!Array.isArray(this.getSomeWareData) || this.getSomeWareData.length > 0);
    },

    uploadProgress() {
      const totalSteps = this.traceCodeUploadSteps.length;
      return Math.floor((this.traceCodeUploadStep / totalSteps) * 100);
    },
    uploadStatus() {
      if (this.traceCodeHasError) return 'exception';
      return 'success';
    }
  },
  watch: {
    visible: {
      immediate: true,
      handler(newVal) {
        this.dialogVisible = newVal;
        if (newVal) {
          this.initData();
          this.loadSuppliers();
        }
      }
    },
    medicineList: {
      handler: function(newVal) {
        this.calculateSummary();
      },
      deep: true
    },
    'formData.allocateType': {
      handler(newVal) {
        if (newVal === 1) {
          this.formData.shedId = 1;
          this.formData.phayId = 3;
        } else {
          this.formData.shedId = 3;
          this.formData.phayId = 1;
        }
        this.loadDrugOptions();
      }
    }
  },
  mounted() {
    this.loadDrugOptions();
  },
  beforeDestroy() {
    if (this.searchTimer) {
      clearTimeout(this.searchTimer);
      this.searchTimer = null;
    }
    if (this.quantityDebounceTimer) {
      clearTimeout(this.quantityDebounceTimer);
      this.quantityDebounceTimer = null;
    }
    if (this.unbindAdminKeyListener && typeof this.unbindAdminKeyListener === 'function') {
      this.unbindAdminKeyListener();
    }
    this.adminModeWindow = false;
    if (this.adminWindowTimer) {
      clearTimeout(this.adminWindowTimer);
      this.adminWindowTimer = null;
    }
    if (this.progressTimer) {
      clearInterval(this.progressTimer);
      this.progressTimer = null;
    }
    if (this.autoCloseTimer) {
      clearTimeout(this.autoCloseTimer);
      this.autoCloseTimer = null;
    }
  },
  methods: {
    // ============================================================
    // 工具方法
    // ============================================================
    formatCurrency(value) {
      if (value === null || value === undefined || isNaN(value)) return '0';
      const num = parseFloat(value);
      if (isNaN(num)) return '0';
      const parts = num.toString().split('.');
      let integerPart = parts[0];
      let decimalPart = parts[1] || '';
      integerPart = integerPart.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
      if (decimalPart) {
        decimalPart = decimalPart.length > 8 ? decimalPart.substring(0, 8) : decimalPart;
        return `${integerPart}.${decimalPart}`;
      } else {
        return integerPart;
      }
    },
    formatDecimal(value, maxDecimals = 8) {
      if (value === null || value === undefined) return '0';
      const num = parseFloat(value);
      if (isNaN(num)) return '0';
      const decimalPlaces = (num.toString().split('.')[1] || '').length;
      const places = Math.min(decimalPlaces, maxDecimals);
      return num.toFixed(places);
    },
    delay(ms) {
      return new Promise(resolve => setTimeout(resolve, ms));
    },
    getDrugTypeText(type) {
      const map = { 1: '西药', 2: '中药', 3: '卫材' };
      return map[type] || '未知';
    },
    getDrugTypeTagType(type) {
      const map = { 1: 'success', 2: 'warning', 3: 'danger' };
      return map[type] || 'info';
    },

    // ============================================================
    // 加载数据
    // ============================================================
    async loadDrugOptions() {
      try {
        this.drugSearchLoading = true;
        const params = {
          pageNum: 1,
          pageSize: 1000,
          drugName: this.drugSearchQuery || '',
        };
        let response;
        if (this.formData.allocateType === 1) {
          response = await selectDrug(params);
        } else {
          response = await selectYFtoYKDrug(params);
        }
        if (response && response.code === 200) {
          this.processDrugOptions(response.data);
        } else {
          this.$message.error('获取药品列表失败');
        }
      } catch (error) {
        console.error('加载药品选项失败:', error);
        this.$message.error('加载药品选项失败');
      } finally {
        this.drugSearchLoading = false;
      }
    },
    processDrugOptions(data) {
      const dataList = data.content || data.list || [];
      const drugs = dataList.map(item => this.mapDrugData(item));
      const grouped = {};
      drugs.forEach(drug => {
        const key = drug.drugName;
        if (!grouped[key]) grouped[key] = [];
        grouped[key].push(drug);
      });
      this.drugOptions = Object.keys(grouped).map(drugName => ({
        label: drugName,
        options: grouped[drugName].map(drug => ({
          ...drug,
          value: drug.id,
          label: this.getBatchOptionLabel(drug),
          drugName: drug.drugName,
          drugId: drug.drugId,
          originalId: drug.id,
          manuLotnum: drug.manuLotnum,
          manufacturer: drug.manufacturer || drug.prodentpName || '未知厂家'
        }))
      }));
      this.allDrugOptions = this.drugOptions.reduce((acc, group) => acc.concat(group.options), []);
    },
    mapDrugData(item) {
      const base = {
        id: item.invyId || item.id || item.dugInvyId,
        originalId: item.invyId || item.id || item.dugInvyId,
        drugId: item.drugId,
        drugName: item.drugName,
        drugSpec: item.drugSpec,
        drugSpecifications: item.drugSpec,
        manuLotnum: item.manuLotnum,
        purcCnt: item.purcCnt,
        realPurcCnt: item.realPurcCnt,
        purcPric: item.purcPric,
        salePric: item.salePric,
        pacCnt: item.pacCnt || 1,
        // 单位映射：优先使用 minUntName / pacUntName
        dosUnt: item.minUntName || item.dosUnt || '',
        pacUnt: item.pacUntName || item.pacUnt || '',
        supplierName: item.supplierName,
        manufacturer: item.manufacturer || item.prodentpName || item.supplierName,
        prodentpName: item.prodentpName || item.manufacturer || item.supplierName,
        expyDate: item.expyDate || '',
        manuDate: item.manuDate || '',
        drugType: item.drugType || 1,
        isBar: item.isBar || 0,
        manufacturerId: item.manufacturerId || item.prodentpNameId || '',
        manufacturerName: item.manufacturer || item.prodentpName || ''
      };
      return base;
    },
    getBatchOptionLabel(item) {
      if (item.manuLotnum) return `${item.drugName} - 批次: ${item.manuLotnum}`;
      else return `${item.drugName} - 无批次号`;
    },
    loadSuppliers() {},
    handleSupplierChange() {},
    handleAddSupplier() {},
    handleSaveSupplier() {},

    // ============================================================
    // 表单初始化
    // ============================================================
    initData() {
      if (this.mode === 'add') {
        this.resetForm();
      } else if (this.rowData) {
        this.fillDataFromRowData();
      }
      if (this.$refs.inboundForm) {
        this.$refs.inboundForm.clearValidate();
      }
    },
    resetForm() {
      const userInfo = JSON.parse(localStorage.getItem('userInfo') || '{}');
      this.formData = {
        allocateType: 1,
        busDate: new Date().toISOString().split('T')[0],
        operationPerson: userInfo.staffName,
        remark: '',
        shedId: 1,
        phayId: 3
      };
      this.medicineList = [];
      this.totalApllyQty = 0;
      this.totalCost = 0;
      this.totalRetail = 0;
      this.allowReselectAll = false;
      this.originalDrugDetails = null;
      this.resetDrugSearch();
      this.loadDrugOptions();
    },
    async fillDataFromRowData() {
      if (!this.rowData) return;
      console.log('从行数据填充表单，行数据:', this.rowData);
      this.originalDrugDetails = this.rowData;
      this.formData = {
        allocateType: this.rowData.allocateType || 1,
        busDate: this.rowData.invoDate || new Date().toISOString().split('T')[0],
        operationPerson: this.rowData.createName,
        remark: this.rowData.remarks || '',
        shedId: this.rowData.shedId || 1,
        phayId: this.rowData.phayId || 3
      };
      if (this.mode !== 'add' && this.rowData.list && this.rowData.list.length > 0) {
        await this.ensureDrugsInOptions();
        this.medicineList = [];
        for (const item of this.rowData.list) {
          const newMedicine = await this.createMedicineFromRowData(item);
          this.medicineList.push(newMedicine);
        }
      }
      this.calculateSummary();
      console.log('药品明细填充完成:', this.medicineList);
    },

    // ============================================================
    // 药品搜索与选项
    // ============================================================
    resetDrugSearch() {
      this.drugSearchQuery = '';
      this.drugPageNum = 1;
      this.drugHasMore = true;
    },
    canSearchDrug(row) {
      // 新增或编辑模式都允许搜索
      if (this.mode === 'add' || this.mode === 'edit') {
        return true;
      }
      return false;
    },
    canRemoteSearch(row) {
      if (!row) return false;
      return this.canSearchDrug(row);
    },
    canClearDrug(row) {
      if (!row) return false;
      // 新增模式或编辑模式都允许清空
      if (this.mode === 'add' || this.mode === 'edit') {
        return true;
      }
      return false;
    },
    canSelectDrug(row) {
      // 新增模式或编辑模式都允许选择
      if (this.mode === 'add' || this.mode === 'edit') {
        return true;
      }
      return false;
    },
    getDrugOptionsForRow(row) {
      if (!row) return [];
      if (this.isViewMode || this.isSubmitMode || this.isReviewMode || this.isPassMode || this.isRollbackMode || this.isFastMode) {
        if (row.drugId) {
          const foundDrug = this.allDrugOptions.find(drug => drug.id === row.drugId);
          if (foundDrug) return [foundDrug];
          else {
            // 降级：使用行数据构建对象，确保包含 drugName
            return [{
              id: row.drugId,
              drugId: row.drugId,
              drugName: row.drugName || '未知药品',
              label: row.drugName || row.drugId,
              drugSpecifications: row.spec,
              pacSpec: row.packageSpec,
              drugType: row.drugType || 1
            }];
          }
        }
        return [];
      }
      // 新增或编辑模式：如果有搜索关键词则显示搜索结果，否则显示全部药品
      if (row.drugSearchQuery && row.drugSearchQuery.trim()) {
        return row.drugSearchResults || [];
      }
      // 编辑模式：返回全部药品，若为空则尝试加载
      if (this.mode === 'edit' && this.allDrugOptions.length === 0) {
        this.loadDrugOptions();
      }
      return this.allDrugOptions || [];
    },
    getDrugFromOriginalData(row) {
      if (this.rowData && this.rowData.list) {
        const originalItem = this.rowData.list.find(item => item.drugId === row.drugId || item.id === row.id);
        if (originalItem && originalItem.drugName) {
          return [{ id: row.drugId, drugName: originalItem.drugName, label: originalItem.drugName, drugSpecifications: originalItem.drugSpec, pacSpec: originalItem.pacSpec, drugType: originalItem.drugType || 1 }];
        }
      }
      return [];
    },
    getDrugDisplayLabel(drug, row) {
      // 不再依赖 row，直接返回 drug 本身的名称
      if (drug.drugName) return drug.drugName;
      if (drug.label) return drug.label;
      return '未知药品';
    },
    getHighlightedDrugName(drug, row) {
      const drugName = this.getDrugDisplayLabel(drug, row) || '';
      if (this.currentSearchKeyword && this.currentSearchKeyword.trim()) {
        return this.highlightKeyword(drugName, this.currentSearchKeyword);
      }
      return drugName;
    },
    highlightKeyword(text, keyword) {
      if (!text || !keyword || !keyword.trim()) return text || '';
      const searchText = String(text);
      const searchKeyword = String(keyword).trim();
      if (!searchKeyword) return searchText;
      try {
        const regex = new RegExp(`(${this.escapeRegExp(searchKeyword)})`, 'gi');
        return searchText.replace(regex, '<span class="highlight-keyword">$1</span>');
      } catch (error) {
        return searchText;
      }
    },
    escapeRegExp(string) {
      return string.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    },
    showNoSearchResults(row) {
      if (!row) return false;
      if ((this.mode === 'add' || (this.mode === 'edit' && row.isNewRow))) {
        const hasSearchQuery = row.drugSearchQuery && row.drugSearchQuery.trim() || this.drugSearchQuery && this.drugSearchQuery.trim();
        const hasSearchResults = row.drugSearchResults && row.drugSearchResults.length > 0;
        return hasSearchQuery && !hasSearchResults;
      }
      return false;
    },
    canLoadMore(row) {
      if (!row) return false;
      if (!this.drugHasMore || this.drugSearchLoading || this.loadingMoreDrugs) return false;
      // 新增模式允许加载更多
      if (this.mode === 'add') return true;
      // 编辑模式允许所有行加载更多（包括已有行和新增行）
      if (this.mode === 'edit') return true;
      return false;
    },
    loadMoreDrugs(row) {
      if (this.canLoadMore(row) && !this.drugSearchLoading && !this.loadingMoreDrugs) {
        this.loadDrugOptions();
      }
    },
    handleDrugSearch(query, row) {
      this.currentSearchKeyword = query || '';
      if (!this.canSearchDrug(row)) return;
      this.drugSearchQuery = query || '';
      // 编辑模式或新增模式都设置 row 的搜索词和结果
      if (this.mode === 'add' || this.mode === 'edit') {
        row.drugSearchQuery = query || '';
      }
      if (!query || query.trim() === '') {
        this.currentSearchKeyword = '';
        if (row.isNewRow || this.mode === 'add') {
          row.drugSearchResults = [];
        }
        this.resetDrugSearch();
        this.loadDrugOptions();
        return;
      }
      this.drugPageNum = 1;
      this.drugHasMore = true;
      this.drugSearchLoading = true;
      if (row.isNewRow || this.mode === 'add') {
        row.drugLoading = true;
      }
      if (this.searchTimer) clearTimeout(this.searchTimer);
      this.searchTimer = setTimeout(async () => {
        await this.performDrugSearch(query, row);
      }, 300);
    },
    async performDrugSearch(query, row) {
      try {
        const searchParams = {
          pageNum: this.drugPageNum,
          pageSize: this.drugPageSize,
          drugName: query.trim(),
        };
        let response;
        if (this.formData.allocateType === 1) {
          response = await selectDrug(searchParams);
        } else {
          response = await selectYFtoYKDrug(searchParams);
        }
        if (response && response.code === 200) {
          const rawDrugs = response.data?.content || response.data || [];
          const mappedDrugs = rawDrugs.map(drug => this.mapDrugData(drug));
          // 编辑模式或新增模式都设置搜索结果
          if (this.mode === 'add' || this.mode === 'edit') {
            row.drugSearchResults = mappedDrugs;
          }
          this.drugHasMore = rawDrugs.length >= this.drugPageSize;
          if (rawDrugs.length === 0) {
            this.$message.info(`未找到 "${query}" 的相关药品`);
          }
        } else {
          this.$message.error(response?.message || '搜索药品失败');
        }
      } catch (error) {
        console.error('执行药品搜索失败:', error);
        this.$message.error('搜索药品失败: ' + (error.message || '网络错误'));
      } finally {
        this.drugSearchLoading = false;
        if (row.isNewRow || this.mode === 'add') {
          row.drugLoading = false;
        }
        // 强制更新视图
        this.$forceUpdate();
      }
    },
    updateDrugOptionsAfterSearch(newDrugs) {
      const mappedDrugs = newDrugs.map(drug => ({
        ...drug,
        id: drug.id || drug.drugId,
        drugType: drug.drugType || 1
      }));
      if (this.drugPageNum === 1) {
        this.drugOptions = mappedDrugs;
        this.allDrugOptions = [...mappedDrugs];
      } else {
        const existingIds = new Set(this.drugOptions.map(d => d.id));
        const uniqueNewDrugs = mappedDrugs.filter(d => !existingIds.has(d.id));
        this.drugOptions = [...this.drugOptions, ...uniqueNewDrugs];
        this.allDrugOptions = [...this.allDrugOptions, ...uniqueNewDrugs];
      }
      this.drugHasMore = newDrugs.length >= this.drugPageSize;
    },
    handleDrugFocus(row) {
      if (!row) return;
      // 在新增或编辑模式下，若 allDrugOptions 为空则加载
        if ((this.mode === 'add' || this.mode === 'edit') && this.allDrugOptions.length === 0) {
          this.loadDrugOptions();
        }
    },
    handleDrugScroll(row) {
      if (this.canLoadMore(row) && !this.drugSearchLoading && !this.loadingMoreDrugs) {
        this.loadMoreDrugs(row);
      }
    },
    handleDrugClear(row) {
      console.log('药品选择框被清空:', row);
      this.resetRowDrugInfo(row);
      if (row.isNewRow || this.mode === 'add') {
        row.drugSearchQuery = '';
        row.drugSearchResults = [];
      }
      this.resetDrugSearch();
      // 清空后重新加载全部药品
      this.loadDrugOptions();
      this.$message.info('已清空药品选择，可重新选择');
    },
    resetRowDrugInfo(row) {
      row.drugId = '';
      row.drugName = '';
      row.drugSpec = '';
      row.packageSpec = '';
      row.pacCnt = 1;
      row.dosUnt = '';
      row.pacUnt = '';
      row.unitLabel = row.purcType === 1 ? row.dosUnt : row.pacUnt;
      row.totalCost = 0;
      row.totalRetail = 0;
      row.manufacturerId = '';
      row.prodentpName = '';
      row.isBar = 0;
      row.drugType = 1;
      row.purcCnt = '';
      row.realPurcCnt = '';
      row.traceStockQty = '';
      row.purcPric = 0;
      row.salePric = 0;
      row.manuLotnum = '';
      row.manuDate = '';
      row.expyDate = '';
      row.traceCodes = [];
      row.tracCodeJson = '';
      row.traceCodeButtonText = '追溯码';
      if (row.isNewRow || this.mode === 'add') {
        row.drugSearchQuery = '';
        row.drugSearchResults = [];
      }
    },

    // ============================================================
    // 药品选择变化
    // ============================================================
    async handleDrugChange(row) {
      console.log('药品选择变化:', row);
      if (!row.drugId) {
        console.log('药品ID为空，可能用户清空了选择，重置药品信息');
        this.resetRowDrugInfo(row);
        return;
      }
    
      const targetId = String(row.drugId);
      let selectedDrug = null;
    
      if (row.drugSearchResults && row.drugSearchResults.length > 0) {
        selectedDrug = row.drugSearchResults.find(
          item => String(item.id) === targetId || String(item.drugId) === targetId
        );
      }
    
      if (!selectedDrug) {
        selectedDrug = this.allDrugOptions.find(
          item => String(item.id) === targetId || String(item.drugId) === targetId
        );
      }
    
      if (selectedDrug) {
        console.log('选择了新药品:', selectedDrug);
        row.id = selectedDrug.id;
        row.drugId = selectedDrug.drugId;
        row.drugName = selectedDrug.drugName;
        row.drugSpec = selectedDrug.drugSpecifications || selectedDrug.drugSpec || '';
        row.packageSpec = selectedDrug.pacSpec || '';
        row.purcCnt = 1;
        row.realPurcCnt = selectedDrug.realPurcCnt || '';
        // 正确赋值单位字段
        row.dosUnt = selectedDrug.dosUnt || '';
        row.pacUnt = selectedDrug.pacUnt || '';
        row.isBar = selectedDrug.isBar || 0;
        row.drugType = selectedDrug.drugType || 1;
        row.manufacturerId = selectedDrug.manufacturerId || '';
        row.prodentpName = selectedDrug.prodentpName || '';
        // 根据 purcType 更新单位标签
        this.updateUnitLabel(row);
        row.purcPric = selectedDrug.purcPric || 0;
        row.salePric = selectedDrug.salePric || 0;
        row.purcCnt = selectedDrug.purcCnt || '';
        row.traceStockQty = '';
        row.apllyAmt = 1;
        row.manuLotnum = selectedDrug.manuLotnum || '';
        row.manuDate = selectedDrug.manuDate || '';
        row.expyDate = selectedDrug.expyDate || '';
        row.traceCodes = [];
        row.tracCodeJson = '';
        row.traceCodeButtonText = row.isBar === 1 ? '追溯码' : '追溯码';
        this.calculateRowAmount(row);
        this.validateRowStock(row);
      } else {
        this.$message.warning('未找到该药品详细信息，请重新选择');
        this.resetRowDrugInfo(row);
      }
    },

    // ============================================================
    // 库存类型变化
    // ============================================================
    handlePurcTypeChange(row) {
      console.log('库存类型变化:', row.purcType);
      this.updateUnitLabel(row);
      this.validateRowStock(row);
    },
    updateUnitLabel(row) {
      if (row.purcType === 1) {
        row.unitLabel = row.dosUnt || '';
      } else if (row.purcType === 2) {
        row.unitLabel = row.pacUnt || '';
      } else {
        row.unitLabel = '';
      }
    },

    // ============================================================
    // 申请数量输入防抖
    // ============================================================
    handleQuantityInput(row) {
      if (this.quantityDebounceTimer) {
        clearTimeout(this.quantityDebounceTimer);
        this.quantityDebounceTimer = null;
      }
      this.quantityDebounceTimer = setTimeout(() => {
        this.validateRowStock(row);
        this.quantityDebounceTimer = null;
      }, 500);
    },
    handleQuantityBlur(row) {
      this.validateRowStock(row);
    },

    // ============================================================
    // 库存验证
    // ============================================================
    async validateRowStock(row) {
      if (!row.drugId) {
        row.validated = false;
        return;
      }
      if (!row.apllyAmt || row.apllyAmt < 1) {
        row.validated = false;
        return;
      }
      try {
        row.validating = true;
        const allocateType = this.formData.allocateType;
        const params = {
          allotNum: row.apllyAmt,
          purcType: row.purcType || 2,
          id: row.id,
          allocateType: allocateType
        };
        const result = await dpurcallocateJoin(params);
        if (result.code === 200) {
          row.validated = true;
          const data = result.data;
          row.realPurcCnt = data.realPurcCnt || data.purcCnt || 0;
          row.purcPric = data.purcPric || row.purcPric || 0;
          row.salePric = data.salePric || row.salePric || 0;
          row.pacCnt = data.pacCnt || row.pacCnt || 1;
          // 从接口更新单位名称
          row.dosUnt = data.minUntName || data.dosUnt || row.dosUnt || '';
          row.pacUnt = data.pacUntName || data.pacUnt || row.pacUnt || '';
          row.traceStockQty = data.traceStockQty || '';
          // 重新计算单位标签
          this.updateUnitLabel(row);
          this.calculateRowAmount(row);
          this.calculateSummary();
          this.$message.success(`${row.drugName} 库存验证成功`);
        } else {
          row.validated = false;
          this.$message.error(result.message || '库存验证失败');
        }
      } catch (error) {
        console.error('库存验证失败:', error);
        row.validated = false;
        this.$message.error('库存验证失败');
      } finally {
        row.validating = false;
      }
    },

    // ============================================================
    // 计算行金额
    // ============================================================
    calculateRowAmount(row) {
      const qty = parseFloat(row.apllyAmt) || 0;
      const cost = parseFloat(row.purcPric) || 0;
      const sale = parseFloat(row.salePric) || 0;
      row.totalCost = qty * cost;
      row.totalRetail = qty * sale;
    },
    calculateSummary() {
      this.totalApllyQty = this.medicineList.reduce((sum, item) => sum + (parseFloat(item.apllyAmt) || 0), 0);
      this.totalCost = this.medicineList.reduce((sum, item) => sum + (parseFloat(item.totalCost) || 0), 0);
      this.totalRetail = this.medicineList.reduce((sum, item) => sum + (parseFloat(item.totalRetail) || 0), 0);
    },

    // ============================================================
    // 药品明细行操作
    // ============================================================
    handleAddMedicine() {
      this.medicineList = this.medicineList.filter(item => item !== null);
      console.log('添加药品按钮被点击，当前模式:', this.mode);
      if (this.medicineList.length > 0) {
        const lastRow = this.medicineList[this.medicineList.length - 1];
        if (!lastRow) {
          this.medicineList = this.medicineList.filter(item => item !== null);
          if (this.medicineList.length === 0) {
            this.pushNewMedicine();
            return;
          }
        }
        const lastRowValid = this.medicineList[this.medicineList.length - 1];
        if (!lastRowValid) {
          this.pushNewMedicine();
          return;
        }
        const requiredFields = ['drugId', 'apllyAmt', 'manuLotnum', 'purcPric'];
        for (const field of requiredFields) {
          if (!lastRowValid[field] && lastRowValid[field] !== 0) {
            this.$message.warning(`请先完成当前行的药品选择和数量`);
            return;
          }
        }
        if (!lastRowValid.validated) {
          this.$message.warning('请先完成当前行的库存验证');
          return;
        }
        if (lastRowValid.isBar === 1 && (!lastRowValid.traceCodes || lastRowValid.traceCodes.length === 0)) {
          this.$message.warning(`请先为「${lastRowValid.drugName}」录入追溯码`);
          return;
        }
      }
      this.pushNewMedicine();
    },
    pushNewMedicine() {
      if (this.allDrugOptions && this.allDrugOptions.length > 0) {
        // 已加载
      } else {
        this.loadDrugOptions();
      }
      this.drugSearchQuery = '';
      const newMedicine = {
        id: '',
        drugId: '',
        drugName: '',
        apllyAmt: '',
        purcType: 2,
        pacCnt: 1,
        purcCnt: '',
        traceStockQty: '',
        manuLotnum: '',
        manuDate: '',
        expyDate: '',
        prodentpName: '',
        prodentpNameId: '',
        purcPric: 0,
        salePric: 0,
        totalCost: 0,
        totalRetail: 0,
        drugSpec: '',
        dosUnt: '',
        pacUnt: '',
        unitLabel: '',
        isBar: 0,
        isNewRow: this.isEditMode,
        drugSearchQuery: '',
        drugSearchResults: [],
        drugLoading: false,
        drugType: 1,
        traceCodes: [],
        tracCodeJson: '',
        traceCodeButtonText: '追溯码',
        validated: false,
        validating: false,
        isQuantityEditable: true,
      };
      this.medicineList.push(newMedicine);
      if (this.allDrugOptions.length === 0) {
        this.loadDrugOptions();
      }
    },
    handleRemoveMedicine(index, row) {
      if (this.isEditMode && row.isNewRow && !row.drugId) {
        this.medicineList.splice(index, 1);
        this.$message.success('已移除');
        return;
      }
      const confirmMessage = row.drugName ? `您目前已选择了（${row.drugName}），请确认是否继续移除？` : '确定移除该药品吗?';
      this.$confirm(confirmMessage, '提示', { confirmButtonText: '确定', cancelButtonText: '取消', type: 'warning' })
        .then(() => {
          this.medicineList.splice(index, 1);
          this.$message.success('移除成功');
        }).catch(() => {});
    },

    // ============================================================
    // 从行数据创建药品（编辑/查看时）
    // ============================================================
    async createMedicineFromRowData(item) {
      const allocateType = this.formData.allocateType || 1;
      let inventoryId = 0;
      if (allocateType === 1) {
        inventoryId = item.invyId || 0;
      } else {
        inventoryId = item.dugInvyId || 0;
      }
      if (!inventoryId) {
        inventoryId = item.id || 0;
      }
    
      // 申请数量兼容
      let apllyAmt = 1;
      if (item.apllyAmt !== undefined && item.apllyAmt !== null && item.apllyAmt !== '') {
        apllyAmt = item.apllyAmt;
      } else if (item.purcCnt !== undefined && item.purcCnt !== null && item.purcCnt !== '') {
        apllyAmt = item.purcCnt;
      }
    
      // 单位字段兼容：优先使用 minUntName / pacUntName
      const dosUnt = item.minUntName || item.dosUnt || item.drugDosageUnit || '';
      const pacUnt = item.pacUntName || item.pacUnt || item.drugPackageUnit || '';
    
      // 解析追溯码
      let isBar = item.isBar || 0;
      let traceCodes = item.traceCodes || [];
      if (item.tracCodeJson && item.tracCodeJson.trim() !== '' && item.tracCodeJson !== '[]') {
        try {
          const parsed = JSON.parse(item.tracCodeJson);
          if (Array.isArray(parsed) && parsed.length > 0) {
            traceCodes = parsed.map(p => ({
              traceCode: p.barcode || p.traceCode || '',
              availableQty: p.chgCnt || 1,
            }));
            isBar = 1;
          }
        } catch (e) {
          console.warn('解析 tracCodeJson 失败:', e);
        }
      }
    
      const purcType = item.purcType || 2;
      // 根据 purcType 决定使用哪个单位字段，若字段为空则置空字符串（不硬编码）
      let unitLabel = '';
      if (purcType === 1) {
        unitLabel = dosUnt || '';
      } else if (purcType === 2) {
        unitLabel = pacUnt || '';
      }
    
      const newMedicine = {
        id: inventoryId,
        _rowId: item.id || inventoryId,
        drugId: item.drugId || '',
        drugName: item.drugName || '',
        apllyAmt: apllyAmt,
        purcType: purcType,
        purcCnt: item.purcCnt || 1,
        realPurcCnt: item.realPurcCnt || '',
        traceStockQty: item.traceStockQty || '',
        manuLotnum: item.manuLotnum || '',
        manuDate: item.manuDate || '',
        expyDate: item.expyDate || '',
        prodentpName: item.prodentpName || '',
        prodentpNameId: item.prodentpNameId || '',
        purcPric: item.purcPric || 0,
        pacCnt: item.pacCnt || '',
        salePric: item.salePric || 0,
        totalCost: item.purcAmt || 0,
        totalRetail: item.saleAmt || 0,
        drugSpec: item.drugSpec || '',
        dosUnt: dosUnt,           // 存储最小单位名称
        pacUnt: pacUnt,           // 存储包装单位名称
        unitLabel: unitLabel,     // 根据 purcType 显示对应单位
        isNewRow: false,
        drugType: item.drugType || 1,
        isBar: isBar,
        traceCodes: traceCodes,
        tracCodeJson: item.tracCodeJson || '',
        traceCodeButtonText: isBar === 1 && traceCodes.length > 0 ? `追溯码(${traceCodes.length})` : '追溯码',
        validated: true,
        validating: false,
        isQuantityEditable: true,
      };
    
      await this.ensureDrugDetailsForRow(newMedicine);
      return newMedicine;
    },
    async ensureDrugDetailsForRow(medicine) {
      if (medicine.drugName && medicine.drugName.trim() !== '') return;
      if (medicine.drugId) {
        const foundDrug = this.allDrugOptions.find(drug => drug.id === medicine.drugId);
        if (foundDrug && foundDrug.drugName) {
          medicine.drugName = foundDrug.drugName;
          medicine.drugSpec = foundDrug.drugSpecifications || medicine.drugSpec;
          medicine.dosUnt = foundDrug.dosUnt || '';
          medicine.pacUnt = foundDrug.pacUnt || '';
          medicine.prodentpName = foundDrug.prodentpName || '';
          medicine.isBar = foundDrug.isBar || 0;
          medicine.drugType = foundDrug.drugType || 1;
        }
        if (this.originalDrugDetails && this.originalDrugDetails.list) {
          const orig = this.originalDrugDetails.list.find(item => item.drugId === medicine.drugId);
          if (orig) {
            medicine.drugName = orig.drugName || orig.name || '';
            medicine.drugSpec = orig.drugSpec || '';
            medicine.prodentpName = orig.prodentpName || '';
            medicine.isBar = orig.isBar || 0;
          }
        }
      }
    },
    async ensureDrugsInOptions() {
      if (!this.rowData || !this.rowData.list) return;
      const drugIds = this.rowData.list.filter(item => item.drugId).map(item => item.drugId);
      for (const drugId of drugIds) {
        if (!this.allDrugOptions.some(d => d.id === drugId)) {
          const item = this.rowData.list.find(i => i.drugId === drugId);
          if (item) {
            this.allDrugOptions.push({
              id: drugId,
              drugId: drugId,
              drugName: item.drugName || item.name || '未知',
              drugSpecifications: item.drugSpec || '',
              pacSpec: item.pacSpec || '',
              prodentpName: item.prodentpName || '',
              drugType: item.drugType || 1,
              isBar: item.isBar || 0,
              pacCnt: item.pacCnt || 1,
              dosUnt: item.dosUnt || '',
              pacUnt: item.pacUnt || '',
              purcCnt: item.purcCnt || '',
              purcPric: item.purcPric || 0,
              salePric: item.salePric || 0,
              manuLotnum: item.manuLotnum || '',
              manuDate: item.manuDate || '',
              expyDate: item.expyDate || '',
            });
          }
        }
      }
    },

    // ============================================================
    // 调拨类型切换
    // ============================================================
    handleAllocateTypeChange(value) {
      if (value === 1) {
        this.formData.shedId = 1;
        this.formData.phayId = 3;
      } else {
        this.formData.shedId = 3;
        this.formData.phayId = 1;
      }
      this.loadDrugOptions();
      if (this.medicineList.length > 0) {
        this.$confirm('切换调拨类型将清空已添加的药品列表，是否继续？', '提示', {
          confirmButtonText: '确定',
          cancelButtonText: '取消',
          type: 'warning'
        }).then(() => {
          this.medicineList = [];
          this.calculateSummary();
        }).catch(() => {
          this.formData.allocateType = value === 1 ? 2 : 1;
          this.handleAllocateTypeChange(this.formData.allocateType);
        });
      }
    },

    // ============================================================
    // 追溯码相关功能（新增及完善）
    // ============================================================
    canTraceCode(row) {
      if (this.mode === 'add') {
        if (!row.drugId) return false;
        if (row.isBar !== 1) return false;
        return true;
      }
      if (this.mode === 'edit') {
        // 新增行：需要已选药品且启用追溯码
        if (row.isNewRow) {
          return !!(row.drugId && row.isBar === 1);
        }
        // 已有行：编辑模式下直接允许（无需检查单据状态）
        return !!(row.drugId && row.isBar === 1);
      }
      return false;
    },
    getTraceCodeTooltip(row) {
      if (this.mode === 'add') {
        if (!row.drugId) return '要上传追溯码，请先选择对应的药品信息';
        if (row.isBar === 0) return '当前药品未启用追溯码功能';
        return '';
      }
      if (this.mode === 'edit') {
        if (row.isNewRow) {
          if (!row.drugId) return '请先选择药品';
          if (row.isBar !== 1) return '当前药品未启用追溯码功能';
          return '';
        }
        // 已有行：编辑模式下无额外限制
        if (row.isBar !== 1) return '当前药品未启用追溯码功能';
        return '';
      }
      return '';
    },

    // ---- 外部扫码查询 ----
    async handleOuterScanConfirm() {
      const traceCode = this.outerScanInput.trim();
      if (!traceCode) {
        this.$message.warning('请输入或扫描追溯码');
        return;
      }
      this.outerScanLoading = true;
      try {
        const res = await msfxCodeDetail({ codes: traceCode });
        if (res.code !== 200 || !res.data || res.data.length === 0) {
          this.$message.error('未查询到该追溯码的信息');
          return;
        }
        const traceData = res.data[0];
        // 存储数据并打开详情模态框
        this.currentTraceDetailData = traceData;
        this.currentTraceCode = traceCode;
        this.traceCodeDetailDialogVisible = true;
      } catch (error) {
        console.error('扫码查询失败:', error);
        this.$message.error('扫码查询失败');
      } finally {
        this.outerScanLoading = false;
        this.outerScanInput = '';
      }
    },

    // ---- 追溯码详情取消 ----
    handleCancelTraceCodeDetail() {
      this.$confirm(
        `您当前的追溯码（${this.currentTraceCode}）已查询完成，取消会重新录入/扫码追溯码，是否继续？`,
        '提示',
        {
          confirmButtonText: '确定',
          cancelButtonText: '取消',
          type: 'warning'
        }
      ).then(() => {
        this.traceCodeDetailDialogVisible = false;
        this.currentTraceDetailData = null;
        this.currentTraceCode = '';
        this.$message.info('已取消录入');
      }).catch(() => {});
    },

    // ---- 确认追溯码录入（详情模态框） ----
    async handleConfirmTraceCodeDetail() {
      if (!this.currentTraceDetailData) {
        this.$message.warning('追溯码数据无效');
        this.traceCodeDetailDialogVisible = false;
        return;
      }
      const success = await this.matchAndAddDrugByTraceCode(
        this.currentTraceDetailData,
        this.currentTraceCode
      );
      if (success) {
        this.traceCodeDetailDialogVisible = false;
        this.currentTraceDetailData = null;
        this.currentTraceCode = '';
      } else {
        // 失败时不关闭，保留数据以便重试
      }
    },

    // ---- 根据追溯码匹配并添加药品到明细列表 ----
    async matchAndAddDrugByTraceCode(traceData, traceCode) {
      const drugEntBase = traceData.drugEntBaseDTO || {};
      const produceInfoList = traceData.codeProduceInfoDTO?.produceInfoList || [];
      const produceInfo = produceInfoList[0] || {};
      const pUserEnt = traceData.pUserEntDTO || {};

      const drugName = drugEntBase.physicName;
      const batchNo = produceInfo.batchNo;
      const prepnSpec = drugEntBase.prepnSpec || '1';
      const pacCntFromTrace = this.extractNumberFromString(prepnSpec) || 1;

      if (!drugName) {
        this.$message.error('无法从追溯码中解析药品名称');
        return false;
      }

      this.$message.info(`正在匹配药品：${drugName}，批次号：${batchNo || '无'}...`);

      // 调用 selectDrug 或 selectYFtoYKDrug 根据调拨类型
      let matchedDrug = null;
      let currentPage = 1;
      const pageSize = 100;
      let hasMore = true;
      const allocateType = this.formData.allocateType; // 1:药库=>药房, 2:药房=>药库

      while (hasMore && !matchedDrug) {
        try {
          const params = {
            pageNum: currentPage,
            pageSize: pageSize,
            drugName: drugName
          };
          let response;
          if (allocateType === 1) {
            response = await selectDrug(params);
          } else {
            response = await selectYFtoYKDrug(params);
          }

          if (response.code !== 200) {
            console.error('selectDrug接口返回异常:', response);
            break;
          }

          const dataList = response.data.content || response.data.list || [];
          if (dataList.length === 0) {
            hasMore = false;
            break;
          }

          for (const item of dataList) {
            const itemDrugName = item.drugName;
            const itemBatchNo = item.manuLotnum;
            const itemPacCnt = item.pacCnt || 1;

            const nameMatch = itemDrugName &&
              itemDrugName.trim().toLowerCase() === drugName.trim().toLowerCase();

            let batchMatch = true;
            if (batchNo && batchNo.trim()) {
              batchMatch = itemBatchNo && itemBatchNo.trim() === batchNo.trim();
            }

            const pacCntMatch = itemPacCnt === pacCntFromTrace;

            if (nameMatch && batchMatch && pacCntMatch) {
              matchedDrug = this.mapDrugData(item);
              break;
            }
          }

          const totalSize = response.data.totalSize || response.data.totalElements || 0;
          hasMore = currentPage * pageSize < totalSize;
          currentPage++;
        } catch (error) {
          console.error('调用selectDrug接口失败:', error);
          break;
        }
      }

      if (!matchedDrug) {
        this.$message.error(`未找到匹配的药品：${drugName}，批次号：${batchNo || '无'}，包装数量：${pacCntFromTrace}`);
        return false;
      }

      // 调用库存验证接口，获取实际库存数量（以最小单位、申请数量1去验证）
      let realStockQty = matchedDrug.purcCnt || 0;
      try {
        const params = {
          allotNum: 1,
          purcType: 1,
          id: matchedDrug.id,
          allocateType: allocateType
        };
        const stockResult = await dpurcallocateJoin(params);
        if (stockResult.code === 200 && stockResult.data) {
          realStockQty = stockResult.data.realPurcCnt || stockResult.data.purcCnt || 0;
          matchedDrug.realPurcCnt = realStockQty;
        } else {
          console.warn('库存验证接口返回异常，使用备选库存值:', stockResult);
        }
      } catch (error) {
        console.error('调用库存验证接口失败，使用备选库存值:', error);
      }

      // 调用 selBar 接口获取追溯码库存数量
      let barCnt = 1;
      let loadingInstance = null;
      try {
        loadingInstance = this.$loading({
          lock: true,
          text: `正在查询当前追溯码（${traceCode}）库存数量...`,
          background: 'rgba(0, 0, 0, 0.7)'
        });

        const selBarParams = {
          drugId: matchedDrug.drugId,
          barcode: traceCode,
          manuLotnum: batchNo || '',
          type: 1  // 西药库
        };
        const selBarResult = await selBar(selBarParams);
        if (selBarResult.code === 200 && selBarResult.data) {
          barCnt = selBarResult.data.barCnt || 1;
          console.log('selBar接口返回追溯码库存:', barCnt);
        } else {
          this.$message.error(selBarResult.message || '查询追溯码库存失败');
          return false;
        }
      } catch (error) {
        console.error('调用selBar接口失败:', error);
        this.$message.error('查询追溯码库存失败，请重试');
        return false;
      } finally {
        if (loadingInstance) {
          loadingInstance.close();
        }
      }

      // 构建追溯码数据
      const traceCodeItem = {
        traceCode: traceCode,
        availableQty: barCnt
      };

      // 构建追溯码JSON
      const tracCodeJson = this.generateTracCodeJson(
        [traceCodeItem],
        1,  // 库存类型为最小单位
        pacCntFromTrace,
        batchNo || ''
      );

      // 创建新行（最小单位）
      const newMedicine = {
        id: matchedDrug.id,
        drugId: matchedDrug.drugId,
        drugName: matchedDrug.drugName,
        drugSpec: matchedDrug.drugSpec,
        apllyAmt: realStockQty,
        purcType: 1,
        isQuantityEditable: false,
        purcPric: matchedDrug.purcPric || 0,
        salePric: matchedDrug.salePric || 0,
        totalCost: (realStockQty * (matchedDrug.purcPric || 0)),
        totalRetail: (realStockQty * (matchedDrug.salePric || 0)),
        validated: true,
        validating: false,
        currentUnit: matchedDrug.dosUnt || '最小单位',
        pacCnt: matchedDrug.pacCnt || 1,
        dosUnt: matchedDrug.dosUnt || '最小单位',
        pacUnt: matchedDrug.pacUnt || '包装单位',
        prodentpName: matchedDrug.prodentpName || '',
        manuLotnum: matchedDrug.manuLotnum || '',
        expyDate: matchedDrug.expyDate || '',
        manuDate: matchedDrug.manuDate || '',
        supplierName: matchedDrug.supplierName || '',
        oldPurcType: 1,
        selectedTraceCount: 1,
        selectedTraceTotalQty: barCnt,
        traceStockQty: barCnt,
        isEmpty: false,
        traceCodes: [traceCodeItem],
        tracCodeJson: tracCodeJson,
        isNewRow: this.isEditMode,
        drugSearchQuery: '',
        drugSearchResults: [],
        drugLoading: false,
        drugType: matchedDrug.drugType || 1,
        isBar: matchedDrug.isBar || 0,
        traceCodeButtonText: '追溯码',
        unitLabel: matchedDrug.dosUnt || '最小单位'
      };

      // 添加到明细列表
      this.medicineList.push(newMedicine);
      this.calculateSummary();
      this.$message.success(`已成功添加药品：${matchedDrug.drugName}，调拨数量：${realStockQty}${newMedicine.currentUnit}，追溯码库存：${barCnt}`);

      // 确保药品选项列表中存在该药品（以便后续编辑）
      if (!this.drugOptions.some(group =>
        group.options.some(opt => opt.id === matchedDrug.id)
      )) {
        const drugGroup = this.drugOptions.find(g => g.label === matchedDrug.drugName);
        if (drugGroup) {
          if (!drugGroup.options.some(opt => opt.id === matchedDrug.id)) {
            drugGroup.options.push({
              ...matchedDrug,
              value: matchedDrug.id,
              label: this.getBatchOptionLabel(matchedDrug)
            });
          }
        } else {
          this.drugOptions.push({
            label: matchedDrug.drugName,
            options: [{
              ...matchedDrug,
              value: matchedDrug.id,
              label: this.getBatchOptionLabel(matchedDrug)
            }]
          });
        }
        // 更新 allDrugOptions
        this.allDrugOptions = this.drugOptions.reduce((acc, group) => acc.concat(group.options), []);
      }

      return true;
    },

    // ---- 生成追溯码 JSON 字符串 ----
    generateTracCodeJson(traceCodes, purcType, pacCnt, manuLotnum) {
      if (!traceCodes || traceCodes.length === 0) return '';
      const arr = traceCodes.map(item => ({
        barcode: item.traceCode,
        chgCnt: item.availableQty || 1,
        manuLotnum: manuLotnum || ''
      }));
      return JSON.stringify(arr);
    },

    // ---- 提取字符串中的数字 ----
    extractNumberFromString(str) {
      if (!str) return 1;
      const match = str.match(/\d+(\.\d+)?/);
      return match ? parseFloat(match[0]) : 1;
    },

    // ---- 追溯码录入模态框 - 确认 ----
    handleConfirmTraceCodeInput() {
      // 此方法对应 "追溯码录入" 模态框（traceCodeInputDialogVisible）的确定按钮
      // 根据当前 traceCodeDetailData 和 traceCodeInputForm 添加药品
      if (!this.traceCodeDetailData) {
        this.$message.warning('请先查询追溯码信息');
        return;
      }
      // 使用当前追溯码详情数据添加药品
      this.matchAndAddDrugByTraceCode(
        this.traceCodeDetailData,
        this.traceCodeInputForm.traceCode || this.currentTraceCode
      ).then(success => {
        if (success) {
          this.traceCodeInputDialogVisible = false;
          this.traceCodeDetailData = null;
          this.resetTraceCodeInputForm();
          this.$message.success('药品添加成功');
        }
      });
    },

    // ---- 追溯码录入模态框 - 重置表单 ----
    resetTraceCodeInputForm() {
      this.traceCodeInputForm = {
        traceCode: '',
        drugName: '',
        spec: '',
        inboundQty: 1,
        purcType: 2,
        pacCnt: 1,
        manuLotnum: '',
        manufacturer: '',
        manufacturerId: '',
        approvalLicenceNo: '',
        produceDate: '',
        expireDate: '',
        refEntId: '',
        remark: '',
        dosageFormName: '',
        pacUnt: '',
        dosUnt: ''
      };
    },

    // ---- 其他追溯码方法（占位，保留原有） ----
    // ---- 打开追溯码补录（支持回显） ----
    openTraceCodeSupplement(row, index) {
      console.log('打开追溯码补录，当前行:', row);
      this.currentTraceMedicine = row;
      this.traceCodeSupplementList = [];
      const packageCount = parseInt(row.pacCnt) || 1;
      const drugName = row.drugName || '';
      const drugSpec = row.drugSpec || '';
      const manufacturer = row.prodentpName || row.manufacturerName || '';
      const batchNo = row.manuLotnum || '';
      const produceDate = row.manuDate || '';
      const expireDate = row.expyDate || '';
    
      // 如果行中已有 traceCodes，直接使用；否则尝试从 tracCodeJson 解析
      let existingTraceCodes = row.traceCodes || [];
      if (existingTraceCodes.length === 0 && row.tracCodeJson && row.tracCodeJson.trim() !== '' && row.tracCodeJson !== '[]') {
        try {
          const parsed = JSON.parse(row.tracCodeJson);
          if (Array.isArray(parsed) && parsed.length > 0) {
            existingTraceCodes = parsed.map(p => ({
              traceCode: p.barcode || p.traceCode || '',
              availableQty: p.chgCnt || 1,
            }));
            // 同时更新 row.traceCodes 以便后续保存
            row.traceCodes = existingTraceCodes;
          }
        } catch (e) {
          console.warn('解析 tracCodeJson 失败:', e);
        }
      }
    
      if (existingTraceCodes && existingTraceCodes.length > 0) {
        existingTraceCodes.forEach((trace, idx) => {
          this.traceCodeSupplementList.push({
            id: Date.now() + idx,
            traceCode: trace.traceCode || '',
            drugName: trace.drugName || drugName,
            spec: trace.spec || drugSpec,
            packageSpec: row.packageSpec || '',
            availableQty: trace.availableQty || packageCount,
            manufacturer: trace.manufacturer || manufacturer,
            batchNo: trace.batchNo || batchNo,
            produceDate: trace.produceDate || produceDate,
            expireDate: trace.expireDate || expireDate,
            isEditing: false
          });
        });
        // 更新按钮文字
        row.traceCodeButtonText = `追溯码(${existingTraceCodes.length})`;
      } else {
        // 无追溯码，添加一个空行（编辑模式）
        this.traceCodeSupplementList.push({
          id: Date.now(),
          traceCode: '',
          drugName: drugName,
          spec: drugSpec,
          packageSpec: row.packageSpec || '',
          availableQty: packageCount,
          manufacturer: manufacturer,
          batchNo: batchNo,
          produceDate: produceDate,
          expireDate: expireDate,
          isEditing: true
        });
      }
      this.traceCodeSupplementDialogVisible = true;
    },
    handleCancelTraceCodeSupplement() {
      this.traceCodeSupplementDialogVisible = false;
      this.traceCodeSupplementList = [];
    },
    handleAddSupplementTraceCode() {
      if (!this.currentTraceMedicine) {
        this.$message.warning('未找到当前药品信息！');
        return;
      }
      const packageCount = parseInt(this.currentTraceMedicine.pacCnt) || 1;
      const drugName = this.currentTraceMedicine.drugName || '';
      const drugSpec = this.currentTraceMedicine.drugSpec || '';
      const manufacturer = this.currentTraceMedicine.prodentpName || this.currentTraceMedicine.manufacturerName || '';
      const batchNo = this.currentTraceMedicine.manuLotnum || '';
      const produceDate = this.currentTraceMedicine.manuDate || '';
      const expireDate = this.currentTraceMedicine.expyDate || '';
      this.traceCodeSupplementList.push({
        id: Date.now() + Math.random(),
        traceCode: '',
        drugName: drugName,
        spec: drugSpec,
        packageSpec: this.currentTraceMedicine.packageSpec || '',
        availableQty: packageCount,
        manufacturer: manufacturer,
        batchNo: batchNo,
        produceDate: produceDate,
        expireDate: expireDate,
        isEditing: true
      });
      this.$message.success('已添加新的追溯码行，请填写追溯码');
    },
    handleEditSupplementRow(index) {
      this.traceCodeSupplementList[index].isEditing = true;
    },
    handleCancelSupplementEdit(index) {
      const row = this.traceCodeSupplementList[index];
      if (row.isFirstRow) return; // 如果定义了首行标志，则不可取消？但我们的代码中没有 isFirstRow，可根据情况。
      if (!row.traceCode || row.traceCode.trim() === '') {
        this.traceCodeSupplementList.splice(index, 1);
      } else {
        row.isEditing = false;
      }
    },
    handleSaveSupplementRow(index) {
      const row = this.traceCodeSupplementList[index];
      if (!row.traceCode || row.traceCode.trim() === '') {
        this.$message.warning('请输入追溯码');
        return;
      }
      const chineseRegex = /[\u4e00-\u9fa5]/;
      if (chineseRegex.test(row.traceCode)) {
        this.$message.warning('追溯码不能包含中文');
        return;
      }
      const traceCodes = this.traceCodeSupplementList.map(item => item.traceCode.trim());
      const duplicateIndex = traceCodes.findIndex((code, idx) => idx !== index && code === row.traceCode.trim() && code !== '');
      if (duplicateIndex !== -1) {
        this.$message.warning('追溯码不能重复');
        return;
      }
      row.isEditing = false;
      this.$message.success('保存成功');
    },
    handleRemoveSupplementRow(index) {
      if (this.traceCodeSupplementList.length === 1 && !this.traceCodeSupplementList[0].traceCode) {
        // 如果只有一行且为空，则关闭模态框
        this.traceCodeSupplementDialogVisible = false;
      } else {
        this.traceCodeSupplementList.splice(index, 1);
      }
    },
    handleConfirmTraceCodeSupplement() {
      if (this.traceCodeSupplementList.length === 0) {
        this.$message.warning('请至少录入一个追溯码');
        return;
      }
      const emptyTraceCode = this.traceCodeSupplementList.find(row => !row.traceCode || row.traceCode.trim() === '');
      if (emptyTraceCode) {
        this.$message.warning('请填写所有追溯码');
        return;
      }
      const traceCodes = this.traceCodeSupplementList.map(row => row.traceCode.trim());
      const duplicateSet = new Set();
      const duplicates = [];
      traceCodes.forEach((code, index) => {
        if (duplicateSet.has(code) && code !== '') {
          duplicates.push(code);
        } else {
          duplicateSet.add(code);
        }
      });
      if (duplicates.length > 0) {
        this.$message.warning(`追溯码不能重复: ${duplicates.join(', ')}`);
        return;
      }
      if (this.currentTraceMedicine) {
        const purcType = parseInt(this.currentTraceMedicine.purcType) || 2;
        const packageCount = parseInt(this.currentTraceMedicine.pacCnt) || 1;
        const traceCodesArr = this.traceCodeSupplementList.map(row => ({
          traceCode: row.traceCode,
          drugName: row.drugName,
          spec: row.spec,
          packageSpec: row.packageSpec,
          manufacturer: row.manufacturer,
          batchNo: row.batchNo,
          produceDate: row.produceDate,
          expireDate: row.expireDate,
          availableQty: parseInt(row.availableQty) || 1
        }));
        const tracCodeJson = traceCodesArr.map(item => ({
          barcode: item.traceCode,
          chgCnt: purcType === 2 ? packageCount : item.availableQty,
          manuLotnum: item.batchNo || ''
        }));
        const tracCodeJsonString = JSON.stringify(tracCodeJson);
        this.currentTraceMedicine.traceCodes = traceCodesArr;
        this.currentTraceMedicine.tracCodeJson = tracCodeJsonString;
        this.currentTraceMedicine.traceCodeButtonText = `追溯码(${traceCodesArr.length})`;
        this.$message.success('追溯码补录成功');
        this.traceCodeSupplementDialogVisible = false;
        this.traceCodeSupplementList = [];
      }
    },
    handleTraceCodeDialogClose(done) {
      if (this.traceCodeList.length > 0) {
        this.$confirm('确定关闭追溯码录入吗？未保存的数据将丢失', '提示', {
          confirmButtonText: '确定',
          cancelButtonText: '取消',
          type: 'warning'
        }).then(() => {
          this.resetTraceCodeDialog();
          done();
        }).catch(() => {});
      } else {
        this.resetTraceCodeDialog();
        done();
      }
    },
    handleCancelTraceCode() {
      this.traceCodeDialogVisible = false;
      this.resetTraceCodeDialog();
    },
    resetTraceCodeDialog() {
      this.traceCodeDialogVisible = false;
      this.currentTraceMedicine = null;
      this.traceCodeList = [];
      this.totalTraceQty = 0;
      this.scanInput = '';
    },
    handleConfirmTraceCode() {
      const invalidCodes = this.traceCodeList.filter(item => !item.traceCode || item.traceCode.trim() === '');
      if (invalidCodes.length > 0) {
        this.$message.warning(`有${invalidCodes.length}条追溯码为空，请填写完整`);
        return;
      }
      const inboundQty = this.currentTraceMedicine ? parseInt(this.currentTraceMedicine.apllyAmt) : 0;
      if (this.totalTraceQty !== inboundQty) {
        this.$message.warning(`追溯码总数量（${this.totalTraceQty}）必须等于调拨数量（${inboundQty}）`);
        return;
      }
      if (this.currentTraceMedicine) {
        this.currentTraceMedicine.traceCodes = this.traceCodeList.map(item => ({
          code: item.traceCode,
          availableQty: item.availableQty,
          remark: item.remark,
          status: item.status,
          batchNo: item.batchNo,
          approvalLicenceNo: item.approvalLicenceNo,
          produceDate: item.produceDate,
          expireDate: item.expireDate,
          originalProduceDate: item.originalProduceDate,
          pkgAmount: item.pkgAmount,
          physicTypeDesc: item.physicTypeDesc,
          prepnTypeDesc: item.prepnTypeDesc,
          codeStatus: item.codeStatus,
          packageLevel: item.packageLevel,
          manufacturer: item.manufacturer,
          drugName: item.drugName,
          spec: item.spec
        }));
        // 生成 tracCodeJson
        const tracCodeJson = this.traceCodeList.map(item => ({
          barcode: item.traceCode,
          chgCnt: item.availableQty || 1,
          manuLotnum: item.batchNo || ''
        }));
        this.currentTraceMedicine.tracCodeJson = JSON.stringify(tracCodeJson);
        this.currentTraceMedicine.traceCodeButtonText = `追溯码(${this.traceCodeList.length})`;
        this.$message.success(`已保存${this.traceCodeList.length}条追溯码`);
        this.traceCodeDialogVisible = false;
        this.resetTraceCodeDialog();
      }
    },
    async handleScanInput() {
      if (!this.scanInput || this.scanInput.trim() === '') {
        this.$message.warning('请输入追溯码');
        return;
      }
      const traceCode = this.scanInput.trim();
      const exists = this.traceCodeList.some(item => item.traceCode === traceCode);
      if (exists) {
        this.$message.warning('该追溯码已存在');
        this.scanInput = '';
        return;
      }
      try {
        this.traceCodeLoading = true;
        const response = await msfxCodeDetail({ codes: traceCode });
        if (response && response.code === 200 && response.data && response.data.length > 0) {
          const traceCodeData = response.data[0];
          const drugInfo = traceCodeData.drugEntBaseDTO || {};
          const produceInfo = traceCodeData.codeProduceInfoDTO?.produceInfoList?.[0] || {};
          const entInfo = traceCodeData.pUserEntDTO || {};
          const codeStatus = traceCodeData.codeStatusTypeDTO || {};
          const inboundQty = this.currentTraceMedicine ? parseInt(this.currentTraceMedicine.apllyAmt) : 0;
          const currentTotal = this.totalTraceQty;
          if (currentTotal >= inboundQty) {
            this.$message.warning(`追溯码总数量（${currentTotal}）已达到调拨数量（${inboundQty}），无法添加更多`);
            this.scanInput = '';
            return;
          }
          const pacCnt = parseInt(produceInfo.pkgAmount) || 1;
          const remainingQty = inboundQty - currentTotal;
          const defaultQty = Math.min(pacCnt, remainingQty);
          let remark = '';
          if (produceInfo.produceDateStr) remark += `生产日期: ${produceInfo.produceDateStr}`;
          if (produceInfo.originalExpireDate) remark += (remark ? ', ' : '') + `有效期: ${produceInfo.originalExpireDate}`;
          if (drugInfo.approvalLicenceNo) remark += (remark ? ', ' : '') + `批准文号: ${drugInfo.approvalLicenceNo}`;
          if (codeStatus.codeStatus) remark += (remark ? ', ' : '') + `码状态: ${codeStatus.codeStatus}`;
    
          this.traceCodeList.push({
            id: this.traceCodeList.length + 1,
            traceCode: traceCode,
            drugName: drugInfo.physicName || this.currentTraceMedicine?.drugName || '',
            spec: drugInfo.prepnSpec || drugInfo.pkgSpecCrit || this.currentTraceMedicine?.drugSpec || '',
            availableQty: defaultQty,
            manufacturer: entInfo.entName || this.currentTraceMedicine?.prodentpName || '',
            remark: remark,
            status: 'active',
            batchNo: produceInfo.batchNo || '',
            approvalLicenceNo: drugInfo.approvalLicenceNo || '',
            produceDate: produceInfo.produceDateStr || '',
            expireDate: produceInfo.originalExpireDate || '',
            originalProduceDate: produceInfo.originalProduceDate || '',
            pkgAmount: produceInfo.pkgAmount || '',
            physicTypeDesc: drugInfo.physicTypeDesc || '',
            prepnTypeDesc: drugInfo.prepnTypeDesc || '',
            codeStatus: codeStatus.codeStatus || '',
            packageLevel: traceCodeData.packageLevel || ''
          });
          this.$message.success(`追溯码添加成功，数量：${defaultQty}`);
          this.scanInput = '';
          this.calculateTotalTraceQty();
          const newTotal = this.totalTraceQty;
          if (newTotal >= inboundQty) {
            this.$message.success(`追溯码总数量（${newTotal}）已达到调拨数量（${inboundQty}）`);
          }
        } else {
          this.$message.error(response?.message || '未找到追溯码信息');
        }
      } catch (error) {
        console.error('获取追溯码详情失败:', error);
        this.$message.error('获取追溯码详情失败');
      } finally {
        this.traceCodeLoading = false;
      }
    },
    calculateTotalTraceQty() {
      this.totalTraceQty = this.traceCodeList.reduce((sum, item) => sum + (parseInt(item.availableQty) || 1), 0);
      return this.totalTraceQty;
    },
    async callGetSomeWare(drugName, pacCnt, approvalLicenceNo) {
      if (!drugName || !pacCnt || !approvalLicenceNo) return;
      try {
        this.getSomeWareLoading = true;
        const response = await getSomeWare({ drugName: drugName, pacCnt: pacCnt, approvalNumber: approvalLicenceNo });
        if (response && response.code === 200) {
          if (response.data && response.data !== '' && (!Array.isArray(response.data) || response.data.length > 0)) {
            this.getSomeWareData = response.data;
            this.showBottomAddButton = false;
            this.traceCodeInputForm.pacUnt = response.data.pacUnt || '包装单位';
            this.traceCodeInputForm.dosUnt = response.data.dosUnt || '最小单位';
            if (response.data.manufacturerId) {
              this.traceCodeInputForm.manufacturerId = response.data.manufacturerId;
            }
            this.updateUnitLabel(this.traceCodeInputForm);
          } else {
            this.handleEmptyDrugResponse(drugName);
          }
        } else {
          console.warn('getSomeWare接口返回非200状态:', response?.message);
          this.$message.warning(response?.message || '未找到相关药品信息');
          this.handleEmptyDrugResponse(drugName);
        }
      } catch (error) {
        console.error('调用getSomeWare接口失败:', error);
        this.handleEmptyDrugResponse(drugName);
      } finally {
        this.getSomeWareLoading = false;
      }
    },
    handleEmptyDrugResponse(drugName) {
      this.$confirm(
        `${drugName}该药品未存在于本系统目录中，请先去添加该药品基本信息档案！`,
        '提示',
        { confirmButtonText: '去添加', cancelButtonText: '取消', type: 'warning', center: true }
      ).then(() => {
        this.traceCodeInputList = [];
        this.$nextTick(() => {
          this.openAddDrugDialog(drugName);
        });
      }).catch(() => {
        this.showBottomAddButton = true;
      });
    },
    handleCloseTraceCodeInput() {
      // 关闭追溯码录入模态框时重置
      this.traceCodeInputDialogVisible = false;
      this.resetTraceCodeInputForm();
      this.traceCodeDetailData = null;
    },
    handleCancelTraceCodeInput() {
      this.traceCodeInputDialogVisible = false;
      this.resetTraceCodeInputForm();
      this.traceCodeDetailData = null;
    },
    validateTraceCodeInputForm() { return true; },
    validateTraceCodeCount() { return true; },
    checkIfTraceCodeAlreadyAdded(traceCode) { return false; },
    openAddDrugDialog(drugName) {
      // 如果药品不存在，可以打开新增药品对话框，目前仅提示
      this.$message.info('新增药品功能暂未开放');
    },
    handleAddDrugSuccess(addedDrugData) {},
    handleClearTraceCode() {},
    handleImportTraceCode() {},
    handleExportTraceCode() {},
    async getSystemParameters() { return true; },
    findMsfxEntId() {},
    resetTraceCodeUpload() {},
    addTraceCodeUploadDetail(message, type) {},
    getStepStatus(index) { return 'wait'; },
    getDetailIcon(type) { return 'el-icon-info'; },
    handleUploadCancel() {},
    closeTraceCodeUploadDialog() {},
    resetPassProgress() {},
    updateProgressStep(index, status, title, description) {},
    updateStepProgress(stepIndex, progress) {},
    startProgressAnimation(stepIndex) {},
    stopProgressAnimation(stepIndex) {},
    updateTotalProgress() {},
    formatTotalProgress(percentage) { return ''; },
    handleCloseProgressDialog() {},
    handleProgressDialogClose(done) { done(); },
    async checkNeedUploadTraceCode() {},
    async extractTraceCodes() {},
    async uploadTraceCodes() { return { success: true }; },
    getBillType(putinTypeId) { return 102; },
    handleTraceCodeSetting() {},
    validateTraceCode() {},
    getMaxAvailableQty() { return 999; },
    handleAvailableQtyChange() {},
    handleTraceCodeBlur() {},
    canEditAvailableQty() { return true; },
    handleAvailableQtyBlur() {},
    getCodeStatusType() { return 'info'; },
    handleRetryUpload() {},
    updateUnitLabelForm() {},

    // ============================================================
    // 提交/修改等操作
    // ============================================================
    buildRequestData(state) {
      const allocateType = this.formData.allocateType;
      const detailsVOS = this.medicineList.map(item => {
        let invyId = 0;
        let dugInvyId = 0;
        if (allocateType === 1) {
          invyId = item.id || 0;
          dugInvyId = 0;
        } else {
          dugInvyId = item.id || 0;
          invyId = 0;
        }
        return {
          apllyAmt: parseInt(item.apllyAmt) || 0,
          dugInvyId: dugInvyId,
          invyId: invyId,
          purcType: item.purcType || 2,
          tracCodeJson: item.tracCodeJson || ''
        };
      });

      return {
        allocateType: allocateType,
        costAmt: parseFloat(this.totalCost) || 0,
        detailsVOS: detailsVOS,
        invoDate: this.formData.busDate,
        phayId: this.formData.phayId,
        purcInvoCodg: this.rowData?.docNum || '',
        remarks: this.formData.remark || '',
        saleAmt: parseFloat(this.totalRetail) || 0,
        shedId: this.formData.shedId,
        state: state
      };
    },

    async handleCreate() {
      for (const medicine of this.medicineList) {
        if (!this.validateMedicine(medicine)) {
          return;
        }
      }
      try {
        this.saving = true;
        const valid = await this.$refs.inboundForm.validate();
        if (!valid) { this.saving = false; return; }
        if (this.medicineList.length === 0) {
          this.$message.warning('请至少添加一种药品');
          this.saving = false;
          return;
        }
        const requestData = this.buildRequestData(0);
        const response = await Adddpurcallocate(requestData);
        if (response.code === 200) {
          this.$message.success('调拨单创建成功');
          this.$emit('success');
          this.handleClose();
        } else {
          this.$message.error(response.message || '创建调拨单失败');
        }
      } catch (error) {
        console.error('创建调拨单失败:', error);
        this.$message.error('创建调拨单失败: ' + (error.message || '请检查必填项'));
      } finally {
        this.saving = false;
      }
    },

    async handleSave() {
      try {
        this.saving = true;
        const valid = await this.$refs.inboundForm.validate();
        if (!valid) { this.saving = false; return; }
        if (this.medicineList.length === 0) {
          this.$message.warning('请至少添加一种药品');
          this.saving = false;
          return;
        }
        for (const medicine of this.medicineList) {
          if (!this.validateMedicine(medicine)) {
            this.saving = false;
            return;
          }
        }
        const requestData = this.buildRequestData(0);
        requestData.id = this.rowData.id;
        const response = await editdpurcallocate(requestData);
        if (response.code === 200) {
          this.$message.success('修改保存成功');
          this.$emit('success');
          this.handleClose();
        } else {
          this.$message.error(response.message || '保存修改失败');
        }
      } catch (error) {
        console.error('保存修改失败:', error);
        this.$message.error('保存修改失败: ' + (error.message || '网络错误'));
      } finally {
        this.saving = false;
      }
    },

    async handleSaveAndSubmit() {
      try {
        this.saving = true;
        const valid = await this.$refs.inboundForm.validate();
        if (!valid) { this.saving = false; return; }
        if (this.medicineList.length === 0) {
          this.$message.warning('请至少添加一种药品');
          this.saving = false;
          return;
        }
        for (const medicine of this.medicineList) {
          if (!this.validateMedicine(medicine)) {
            this.saving = false;
            return;
          }
        }
        const draftData = this.buildRequestData(0);
        draftData.id = this.rowData.id;
        const saveResp = await editdpurcallocate(draftData);
        if (saveResp.code !== 200) {
          this.$message.error('保存失败：' + (saveResp.message || ''));
          this.saving = false;
          return;
        }
        const submitResp = await changedpurcallocate({ id: this.rowData.id, state: 2 });
        if (submitResp.code === 200) {
          this.$message.success('保存并提交审核成功');
          this.$emit('success');
          this.handleClose();
        } else {
          this.$message.error('提交审核失败：' + (submitResp.message || ''));
        }
      } catch (error) {
        console.error('保存并提交失败:', error);
        this.$message.error('操作失败: ' + (error.message || '网络错误'));
      } finally {
        this.saving = false;
      }
    },

    async handleSubmitConfirm() {
      this.saving = true;
      try {
        const response = await changedpurcallocate({
          id: this.rowData.id,
          state: 2
        });
        if (response && response.code === 200) {
          this.$message.success('提交审核成功');
          this.$emit('success', response.data);
          this.dialogVisible = false;
        } else {
          this.$message.error(response.message || '提交审核失败');
        }
      } catch (error) {
        console.error('提交审核失败:', error);
        this.$message.error('提交审核失败');
      } finally {
        this.saving = false;
      }
    },

    async handleReviewConfirm() {
      this.saving = true;
      try {
        const response = await changedpurcallocate({
          id: this.rowData.id,
          state: 4
        });
        if (response && response.code === 200) {
          this.$message.success('审核成功');
          this.$emit('success', response.data);
          this.dialogVisible = false;
        } else {
          this.$message.error(response.message || '审核失败');
        }
      } catch (error) {
        console.error('审核失败:', error);
        this.$message.error('审核失败');
      } finally {
        this.saving = false;
      }
    },

    async handlePassConfirm() {
      if (this.mode !== 'pass') return;
      for (const medicine of this.medicineList) {
        if (medicine.isBar === 1 && (!medicine.traceCodes || medicine.traceCodes.length === 0)) {
          this.$message.warning(`药品「${medicine.drugName}」需录入追溯码才能过单`);
          return;
        }
      }
      try {
        this.saving = true;
        this.passProgressDialogVisible = true;
        this.resetTraceCodeUpload();
        this.addTraceCodeUploadDetail('开始准备过单...', 'info');

        this.traceCodeUploadStep = 0;
        this.traceCodeUploadSteps[0].description = '正在检查是否有药品需要录入追溯码...';
        this.addTraceCodeUploadDetail('正在检查是否有药品需要录入追溯码...', 'info');
        await this.checkNeedUploadTraceCode();

        this.traceCodeUploadStep = 1;
        this.traceCodeUploadSteps[1].description = '正在接入码上放心平台接口...';
        this.addTraceCodeUploadDetail('正在接入码上放心平台接口...', 'info');
        const paramSuccess = await this.getSystemParameters();
        if (paramSuccess) {
          this.addTraceCodeUploadDetail('系统参数获取成功', 'success');
          this.traceCodeUploadSteps[1].description = '码上放心平台接口接入成功';
        } else {
          this.addTraceCodeUploadDetail('系统参数获取失败，将使用默认值', 'warning');
          this.traceCodeUploadSteps[1].description = '系统参数获取失败';
        }

        this.traceCodeUploadStep = 2;
        this.traceCodeUploadSteps[2].description = '正在上传调拨明细追溯码数据...';
        this.addTraceCodeUploadDetail('正在上传调拨明细追溯码数据...', 'info');

        let uploadResult = { success: true, skip: false };
        if (this.needUploadTraceCode) {
          await this.extractTraceCodes();
          if (this.traceCodeCount > 0) {
            uploadResult = await this.uploadTraceCodes();
            if (uploadResult.success) {
              this.addTraceCodeUploadDetail(`已成功上传 ${this.traceCodeCount} 条追溯码记录`, 'success');
              this.traceCodeUploadSteps[2].description = `已成功上传 ${this.traceCodeCount} 条追溯码记录`;
              this.msfxUploadSuccess = true;
            } else {
              this.addTraceCodeUploadDetail(`上传失败: ${uploadResult.message}`, 'error');
              this.traceCodeUploadSteps[2].description = '上传失败';
              this.traceCodeHasError = true;
              this.msfxUploadSuccess = false;
            }
          } else {
            this.addTraceCodeUploadDetail('没有有效的追溯码需要上传', 'warning');
            this.traceCodeUploadSteps[2].description = '没有有效的追溯码';
            uploadResult.skip = true;
          }
        } else {
          this.addTraceCodeUploadDetail('没有需要上传追溯码的药品', 'warning');
          this.traceCodeUploadSteps[2].description = '没有需要上传追溯码的药品';
          uploadResult.skip = true;
        }

        this.traceCodeUploadStep = 3;
        if (uploadResult.success && !uploadResult.skip) {
          this.addTraceCodeUploadDetail('追溯码已上传至码上放心平台。', 'success');
          this.traceCodeUploadSteps[3].description = '追溯码已上传至码上放心平台。';
        } else if (!uploadResult.success) {
          this.addTraceCodeUploadDetail('追溯码上传失败，但仍可继续过单流程', 'warning');
          this.traceCodeUploadSteps[3].description = '追溯码上传失败';
        } else {
          this.addTraceCodeUploadDetail('无追溯码需要上传，跳过此步骤', 'info');
          this.traceCodeUploadSteps[3].description = '无追溯码需要上传';
        }

        this.traceCodeUploadStep = 4;
        this.traceCodeUploadSteps[4].description = '正在执行过单操作...';
        this.addTraceCodeUploadDetail('正在执行过单操作...', 'info');

        const passResp = await changedpurcallocate({
          id: this.rowData.id,
          state: 5
        });
        if (passResp.code === 200) {
          this.addTraceCodeUploadDetail('过单成功！', 'success');
          this.traceCodeUploadSteps[4].description = '过单成功！';
          this.showCloseButton = true;
          this.totalProgress = 100;
          this.$message.success('调拨过单完成！');
          this.$emit('success');
          this.autoCloseTimer = setTimeout(() => {
            this.closeTraceCodeUploadDialog();
          }, 2000);
        } else {
          this.addTraceCodeUploadDetail('过单失败：' + (passResp.message || '未知错误'), 'error');
          this.traceCodeUploadSteps[4].description = '过单失败';
          this.traceCodeHasError = true;
          this.showCloseButton = true;
          this.$message.error('过单失败：' + (passResp.message || ''));
        }
      } catch (error) {
        console.error('过单失败:', error);
        this.addTraceCodeUploadDetail('过单异常：' + error.message, 'error');
        this.traceCodeHasError = true;
        this.showCloseButton = true;
        this.$message.error('过单失败: ' + (error.message || '网络错误'));
      } finally {
        this.saving = false;
        this.passLoading = false;
        if (this.progressTimer) {
          clearInterval(this.progressTimer);
          this.progressTimer = null;
        }
      }
    },

    async handleRollbackDraft() {
      if (this.mode !== 'review') return;
      try {
        this.saving = true;
        const response = await changedpurcallocate({
          id: this.rowData.id,
          state: 0
        });
        if (response.code === 200) {
          this.$message.success('已回退到草稿状态');
          this.$emit('success');
          this.handleClose();
        } else {
          this.$message.error(response.message || '回退失败');
        }
      } catch (error) {
        console.error('回退失败:', error);
        this.$message.error('回退失败: ' + (error.message || '网络错误'));
      } finally {
        this.saving = false;
      }
    },

    async handleRollbackReview() {
      if (this.mode !== 'pass') return;
      try {
        this.saving = true;
        const response = await changedpurcallocate({
          id: this.rowData.id,
          state: 2
        });
        if (response.code === 200) {
          this.$message.success('已回退到审核通过状态');
          this.$emit('success');
          this.handleClose();
        } else {
          this.$message.error(response.message || '回退失败');
        }
      } catch (error) {
        console.error('回退失败:', error);
        this.$message.error('回退失败: ' + (error.message || '网络错误'));
      } finally {
        this.saving = false;
      }
    },

    async handleRollbackConfirm() {
      if (this.mode !== 'rollback') return;
      try {
        this.saving = true;
        const response = await changedpurcallocate({
          id: this.rowData.id,
          state: 0
        });
        if (response.code === 200) {
          this.$message.success('回退成功');
          this.$emit('success');
          this.handleClose();
        } else {
          this.$message.error(response.message || '回退失败');
        }
      } catch (error) {
        console.error('回退失败:', error);
        this.$message.error('回退失败: ' + (error.message || '网络错误'));
      } finally {
        this.saving = false;
      }
    },

    async handleFastTransferConfirm() {
      if (this.mode !== 'fast') return;
      try {
        this.saving = true;
        const docNum = this.rowData?.docNum || '';
        if (!docNum) {
          this.$message.error('单据编号不存在');
          this.saving = false;
          return;
        }
        const response = await fastTransfer({ docNum });
        if (response.code === 200) {
          this.$message.success('快速调拨成功');
          this.$emit('success');
          this.handleClose();
        } else {
          this.$message.error(response.message || '快速调拨失败');
        }
      } catch (error) {
        console.error('快速调拨失败:', error);
        this.$message.error('快速调拨失败: ' + (error.message || '网络错误'));
      } finally {
        this.saving = false;
      }
    },

    // ============================================================
    // 校验单行药品必填字段
    // ============================================================
    validateMedicine(medicine) {
      const requiredFields = [
        { field: 'drugId', name: '药品名称' },
        { field: 'apllyAmt', name: '申请调拨数量' },
        { field: 'manuLotnum', name: '批次号' },
        { field: 'purcPric', name: '成本单价' }
      ];
      for (const { field, name } of requiredFields) {
        if (medicine[field] === undefined || medicine[field] === null || medicine[field] === '') {
          this.$message.warning(`请填写「${name}」`);
          return false;
        }
      }
      if (!medicine.validated) {
        this.$message.warning('请先完成库存验证');
        return false;
      }
      if (parseFloat(medicine.apllyAmt) <= 0) {
        this.$message.warning('「申请调拨数量」必须大于0');
        return false;
      }
      const cost = parseFloat(medicine.purcPric);
      if (isNaN(cost) || cost <= 0) {
        this.$message.warning('「成本单价」必须为正数');
        return false;
      }
      return true;
    },

    // ============================================================
    // 关闭
    // ============================================================
    handleClose() {
      this.dialogVisible = false;
      this.$emit('close');
    },
  }
};
</script>

<style lang="less" scoped>
::v-deep .el-dialog {
  border-radius: 10px;
}
::v-deep .el-dialog__body {
  padding: 10px;
}
::v-deep .el-form-item__label {
  line-height: 12px;
  font-weight: 500;
}
.addxyk-dialog {
  border-radius: 8px;
  .el-dialog__title {
    font-size: 16px;
    color: #333;
    font-weight: 600;
  }
  .el-dialog__header {
    background-color: #ffffff !important;
    border-bottom: 1px solid #e8e8e8;
    padding: 12px;
  }
  .el-dialog__headerbtn .el-dialog__close {
    color: #606266;
  }
  .el-dialog__headerbtn .el-dialog__close:hover {
    color: #409EFF;
  }

  .section-title {
    font-size: 16px;
    font-weight: 700;
    color: #303133;
    margin: 0 0 12px 0;
    padding-bottom: 6px;
    display: inline-block;
    border-bottom: 3px solid #409EFF;
  }

  .basic-info-section {
    margin-bottom: 12px;
    border-bottom: 1px solid #e8e8e8;
    .section-header {
      margin-bottom: 16px;
      .section-desc {
        font-size: 12px;
        color: #606266;
        margin: 8px 0 0 0;
        line-height: 1.6;
      }
    }
  }

  .medicine-detail-section {
    .section-header {
      display: flex;
      justify-content: space-between;
      align-items: center;
      margin-bottom: 10px;
    }
  }

  ::v-deep .el-form-item {
    margin-bottom: 8px;
    .el-form-item__label {
      font-weight: 700;
      padding-bottom: 2px;
    }
  }

  .drug-select-container {
    position: relative;
  }
  .drug-option {
    display: flex;
    flex-direction: column;
    padding: 5px 0;
    line-height: 1.4;
    .drug-name {
      font-weight: bold;
      color: #333;
      margin-bottom: 2px;
    }
    .drug-spec {
      font-size: 12px;
      color: #666;
      margin-top: 1px;
      &:not(:last-child) {
        margin-bottom: 1px;
      }
    }
  }
  .load-more-option,
  .no-more-option,
  .loading-option {
    text-align: center;
    padding: 8px 0;
    color: #909399;
  }
  .qty-wrapper {
    display: flex;
    align-items: center;
    .qty-input {
      flex: 1;
      margin-right: 8px;
    }
    .unit-label {
      min-width: 40px;
      color: #666;
      font-size: 12px;
      text-align: left;
    }
  }
  .summary-info {
    margin-top: 10px;
    padding: 12px;
    background-color: #f5f7fa;
    border-radius: 4px;
    .summary-item {
      display: flex;
      justify-content: space-between;
      align-items: center;
      font-size: 14px;
      .label {
        color: #606266;
      }
      .value {
        color: #409EFF;
        font-weight: 600;
      }
    }
  }
}

.drug-select-popper {
  .el-select-dropdown__item {
    height: auto;
    min-height: 50px;
    padding: 8px 12px;
    display: flex;
    align-items: center;
    &:hover,
    &.selected,
    &.hover {
      .drug-option .drug-name .highlight-keyword {
        color: #ff6700;
        background-color: rgba(255,103,0,0.15);
      }
    }
    .drug-option {
      display: flex;
      flex-direction: column;
      width: 100%;
      line-height: 1.4;
      min-height: 50px;
      .drug-name {
        font-weight: 600;
        color: #303133;
        margin-bottom: 3px;
        font-size: 13px;
        .highlight-keyword {
          color: #ff6700;
          font-weight: bold;
          background-color: rgba(255,103,0,0.1);
          padding: 0 2px;
          border-radius: 2px;
        }
      }
      .drug-spec {
        font-size: 12px;
        color: #606266;
        line-height: 1.3;
        &:not(:last-child) {
          margin-bottom: 2px;
        }
      }
    }
  }
}

.trace-code-dialog {
  .el-dialog__header {
    background-color: #f5f7fa !important;
    border-bottom: 1px solid #ebeef5;
  }
  .el-dialog__title {
    color: #303133 !important;
    font-weight: 600;
  }
  .el-dialog__body {
    padding: 0 !important;
    background-color: #ffffff;
  }
  .el-dialog__footer {
    background-color: #ffffff;
    border-top: 1px solid #ebeef5;
    padding: 10px 20px;
  }
}

.trace-code-dialog-content {
  padding: 20px;
}

.function-buttons-section {
  margin-bottom: 20px;
  padding-bottom: 15px;
  border-bottom: 1px solid #ebeef5;
  .function-button-group {
    display: flex;
    justify-content: space-between;
    align-items: center;
    flex-wrap: wrap;
    .left-function-area {
      flex: 1;
      min-width: 300px;
      margin-right: 20px;
      .scan-input-area {
        background-color: #f8f9fa;
        border: 1px solid #ebeef5;
        border-radius: 4px;
        padding: 4px;
        .scan-area-title { font-size: 14px; color: #606266; margin-bottom:10px; font-weight:600; }
        .scan-input-wrapper { margin-bottom:8px; }
        .scan-tips { font-size:12px; color:#909399; i { margin-right:5px; } }
      }
    }
    .right-function-area {
      display: flex;
      justify-content: flex-end;
      align-items: center;
      flex-wrap: wrap;
      min-width: 200px;
      .function-button-item {
        display: flex;
        flex-direction: column;
        align-items: center;
        cursor: pointer;
        padding: 10px 15px;
        border-radius: 4px;
        transition: all 0.3s;
        min-width: 80px;
        margin-left: 10px;
        &:hover { background-color:#f5f7fa; transform:translateY(-2px); }
        &.active { background-color:#ecf5ff; }
        .function-icon { font-size:24px; color:#409EFF; margin-bottom:8px; }
        .function-text { font-size:12px; color:#606266; }
      }
    }
  }
}

.summary-info-section {
  background-color: #f8f9fa;
  border: 1px solid #ebeef5;
  border-radius: 4px;
  padding: 15px;
  margin-bottom: 15px;
  .summary-item {
    display: flex;
    align-items: center;
    .label { font-size:12px; color:#606266; margin-right:8px; white-space:nowrap; }
    .value { font-size:13px; color:#303133; font-weight:500; &.warning { color:#E6A23C; font-weight:600; } }
  }
}

.trace-code-table-section {
  .trace-code-table {
    /deep/ .el-table {
      border: 1px solid #ebeef5;
      th { background-color:#f5f7fa; font-weight:600; color:#303133; }
      td, th { border-right:1px solid #ebeef5; border-bottom:1px solid #ebeef5; }
    }
  }
}

.trace-code-dialog-footer {
  display: flex;
  justify-content: flex-end;
  align-items: center;
  .el-button { margin-left:10px; }
}

.trace-code-upload-dialog {
  .upload-progress-dialog {
    .upload-steps {
      margin-bottom: 20px;
    }
    .upload-details {
      margin-top: 20px;
      border: 1px solid #e6e6e6;
      border-radius: 4px;
      padding: 12px;
      .details-title {
        font-weight: 500;
        margin-bottom: 8px;
        color: #303133;
      }
      .details-content {
        .detail-item {
          padding: 6px 0;
          border-bottom: 1px solid #f0f0f0;
          display: flex;
          align-items: center;
          font-size: 13px;
          &:last-child {
            border-bottom: none;
          }
          i {
            margin-right: 8px;
            font-size: 16px;
          }
          .detail-time {
            margin-left: auto;
            color: #909399;
            font-size: 12px;
          }
          &.success {
            color: #67C23A;
            i { color: #67C23A; }
          }
          &.error {
            color: #F56C6C;
            i { color: #F56C6C; }
          }
          &.warning {
            color: #E6A23C;
            i { color: #E6A23C; }
          }
          &.info {
            color: #909399;
            i { color: #909399; }
          }
        }
      }
    }
    .upload-progress {
      margin-top: 20px;
    }
  }
}

.trace-code-detail-container {
  padding: 10px;
  .trace-code-descriptions {
    margin: 10px 0;
    ::v-deep .el-descriptions__label {
      width: 100px;
      background-color: #f5f7fa;
    }
    ::v-deep .el-descriptions__content {
      word-break: break-all;
    }
  }
}
</style>